import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Platform } from 'react-native';
import WebView from 'react-native-webview';
import { AdminView } from './AdminView';
import { ReportView } from './ReportView';
import { ShareReportModal } from './ShareReportModal';
import { appConfig } from '../../config/configManager';
import { generateFullReportHtml, wrapReportHtml } from '../../utils/reportRenderer';
import { useProfileStore } from '../../contexts/StoreContext';
import { AdminStateDto, AdminDataDto } from '../../types/adminState';
import { AdminStateStorage } from '../../services/AdminStateStorage';
import { AdminAssessmentStorage } from '../../services/AdminAssessmentStorage';
import { AdminAssessmentDto, AdminLaunchRequestDto } from '../../types/adminAssessment';
import { AdminAssessmentStorage as AdminLaunchRequestStorage } from '../../storage/AdminAssessmentStorage';
import { AdminResponseStorage } from '../../storage/AdminResponseStorage';
import { AdminResponseDto } from '../../types/AdminResponseDto';
import { NavigationTab } from '../../types/navigation';
import { WellCheckStorage, WellCheckScheduleDto, WellCheckDto, GrowthDataDto, GrowthTrackDto, VaccineTrackDto, PrescriptionEventDto, VaccineDto, VaccineSchEvntDto } from '../../storage/WellCheckStorage';
import { createMasterMilestoneStorage } from '../../storage/masterMilestoneStorage';
import { AdminActionDto, AppProfilePHIDto, MVarDataDto } from '../../types/api/appProfile';
import { ActionDefStorage, ActionDefDto } from '../../storage/ActionDefStorage';
import { PrescriptionListEditor } from './PrescriptionListEditor';
import type { PrescriptionListEditorHandle } from './PrescriptionListEditor';
import { ReferralListEditor } from './ReferralListEditor';
import type { ReferralListEditorHandle } from './ReferralListEditor';
import { DatePickerModal } from '../DatePickerModal';

interface WellVisitViewProps {
  accountId?: string;
  profileId?: string;
  useContentStore?: any; // Platform-specific content store hook
  storage?: any; // Storage interface for AdminSegmentStorage
  apiService?: any; // ApiService instance
  contentService?: any; // contentService instance
  showDebugInfo?: boolean; // Show debug information
  enableEditPageLogic?: boolean; // Enable page logic editing
  enableEditCalcLogic?: boolean; // Enable calc logic editing
  enableEditReportLogic?: boolean; // Enable report logic editing
  onTabChange?: (tab: NavigationTab) => void; // Navigate to another top-level tab
  onSnapshotTabChange?: (tab: NavigationTab) => void; // Navigate from Snapshot checklist (adds Back to Snapshot)
  initialTab?: WellVisitTab; // Open on a specific tab when mounted
  onViewPdf?: (html: string, filename: string) => void; // Web-only: open report as PDF in new tab
}

type WellVisitTab = 'Schedule' | 'Snapshot' | 'Requests' | 'History';

const WELLVISIT_TABS: { key: WellVisitTab; name: string; tabLabel?: string; icon: string; description: string }[] = [
  { key: 'Schedule', name: 'Well-Check Schedule', tabLabel: 'Schedule', icon: '📅', description: 'Schedule and manage upcoming well visits' },
  { key: 'Snapshot', name: 'Well-Check Snapshot', tabLabel: 'Snapshot', icon: '📋', description: 'Administer a Snapshot assessment' },
  { key: 'Requests', name: 'Assessment Requests', tabLabel: 'Requests', icon: '📝', description: 'Pending and in-process assessment requests' },
  { key: 'History', name: 'Assessment History', tabLabel: 'History', icon: '📊', description: 'Completed assessment history and reports' },
];

export const WellVisitView: React.FC<WellVisitViewProps> = ({ accountId = 'default_account', profileId = 'default_profile', useContentStore, storage, apiService, contentService, showDebugInfo = false, enableEditPageLogic = false, enableEditCalcLogic = false, enableEditReportLogic = false, onTabChange, onSnapshotTabChange, initialTab, onViewPdf }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => initialTab ? Math.max(0, WELLVISIT_TABS.findIndex(t => t.key === initialTab)) : 0);
  const [showAssessment, setShowAssessment] = useState(false);

  useEffect(() => {
    if (!initialTab) return;
    const nextIndex = WELLVISIT_TABS.findIndex(t => t.key === initialTab);
    if (nextIndex >= 0) {
      setSelectedTabIndex(nextIndex);
    }
  }, [initialTab]);
  const [showReport, setShowReport] = useState(false);
  const [availableAssessments, setAvailableAssessments] = useState<AdminStateDto[]>([]);
  const [completedAssessments, setCompletedAssessments] = useState<AdminResponseDto[]>([]);
  const [assessmentDefinitions, setAssessmentDefinitions] = useState<AdminAssessmentDto[]>([]);
  const [providerRequests, setProviderRequests] = useState<AdminLaunchRequestDto[]>([]);
  const [currentAssessmentId, setCurrentAssessmentId] = useState<string | null>(null);
  const [currentAdminState, setCurrentAdminState] = useState<AdminStateDto | null>(null);
  const [selectedReportRecId, setSelectedReportRecId] = useState<string | null>(null);
  const [selectedAssessmentDefinition, setSelectedAssessmentDefinition] = useState<AdminAssessmentDto | null>(null);
  const [hasAdminSegmentContent, setHasAdminSegmentContent] = useState(false);

  // Inline Snapshot Report rendering state
  const [snapshotReportHtml, setSnapshotReportHtml] = useState<string | null>(null);
  const [snapshotReportWebViewHeight, setSnapshotReportWebViewHeight] = useState(0);
  const [showSnapshotShareModal, setShowSnapshotShareModal] = useState(false);


  const profileStore = useProfileStore();
  const { getActiveProfile, updateProfile, markProfileDirty } = profileStore();
  const activeProfile = getActiveProfile();

  // Build PHI DTO from active ChildProfile so report scripts can access name/dob/gender/extras
  const profilePHIExtra: MVarDataDto[] = (() => {
    if (!activeProfile) return [];

    const out = new Map<string, string>();
    const addExtra = (name: string, value: string | number | null | undefined) => {
      if (!name) return;
      if (value === null || value === undefined) return;
      const asText = String(value);
      if (asText === '') return;
      out.set(name, asText);
    };

    // Preserve pre-existing extras if available from hydrated/raw profile payload.
    const rawExtra = (activeProfile as any)?.extra;
    if (Array.isArray(rawExtra)) {
      for (const item of rawExtra) {
        if (item && typeof item.name === 'string') addExtra(item.name, item.value);
      }
    }
    const phiExtra = (activeProfile as any)?.phi?.extra;
    if (Array.isArray(phiExtra)) {
      for (const item of phiExtra) {
        if (item && typeof item.name === 'string') addExtra(item.name, item.value);
      }
    }

    // Snapshot-relevant extras from current profile fields.
    addExtra('cdc_premature', activeProfile.cdc_premature === 1 ? 1 : 0);
    if (activeProfile.cdc_premature === 1) {
      addExtra('cdc_premgest', activeProfile.cdc_premgest);
      addExtra('cdc_premweek', activeProfile.cdc_premweek);
    }

    return Array.from(out.entries()).map(([name, value]) => ({ name, value }));
  })();

  const profilePHI: AppProfilePHIDto | undefined = activeProfile ? {
    dname: activeProfile.name,
    fname: activeProfile.name,
    lname: '',
    email: activeProfile.email ?? '',
    dob: activeProfile.dob,
    gender: activeProfile.gender,
    extra: profilePHIExtra,
  } : undefined;

  // Guard: only show assessment reports when profile has name, dob, and gender known
  const hasPHI = !!(activeProfile?.name?.trim() && activeProfile?.dob?.trim() &&
    activeProfile?.gender && activeProfile.gender !== 'prefer-not-to-say');

  // ── WellCheck schedule state ─────────────────────────────────────────────
  const [wellCheckSchedule, setWellCheckSchedule] = useState<WellCheckScheduleDto | null>(null);
  const [wellCheckVaccines, setWellCheckVaccines] = useState<VaccineDto[]>([]);
  const [editDate, setEditDate] = useState('');
  // Computed due date for the current upcoming event — used to seed the picker when no date is saved
  const [editDateDue, setEditDateDue] = useState('');
  const [editDatePickerFocused, setEditDatePickerFocused] = useState(false);
  const [editDatePickerEventId, setEditDatePickerEventId] = useState<string | null>(null);
  const [editLocation, setEditLocation] = useState('');
  const [editType, setEditType] = useState('In-Office');
  const [editSaving, setEditSaving] = useState(false);
  const [vaccineInfoEnabled, setVaccineInfoEnabled] = useState(true);
  const [trackPrescriptions, setTrackPrescriptions] = useState(true);
  const [trackReferrals, setTrackReferrals] = useState(true);
  const [snapshotEventName, setSnapshotEventName] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [trackHeightWeight, setTrackHeightWeight] = useState(true);
  const [measurementUnits, setMeasurementUnits] = useState<'english' | 'metric'>('english');
  const [actionDef, setActionDef] = useState<ActionDefDto | null>(null);
  // Disposition form fields (ephemeral — committed by handleCompleteVisit)
  const [dispVaccinesChecked, setDispVaccinesChecked] = useState<Set<string>>(new Set());
  const [dispHeight, setDispHeight] = useState('');
  const [dispWeight, setDispWeight] = useState('');
  const [dispWeightOz, setDispWeightOz] = useState('');
  const [dispPrescriptionsEnabled, setDispPrescriptionsEnabled] = useState(false);
  const [dispReferralsEnabled, setDispReferralsEnabled] = useState(false);
  const [dispOutcomeAnswer, setDispOutcomeAnswer] = useState<'' | 'yes' | 'no'>('');
  // Full Schedule inline vaccine editor state
  const [schedVaxEditEventId, setSchedVaxEditEventId] = useState<string | null>(null);
  const [schedVaxEditChecked, setSchedVaxEditChecked] = useState<Set<string>>(new Set());
  // Full Schedule inline height/weight editor state
  const [schedHwEditEventId, setSchedHwEditEventId] = useState<string | null>(null);
  const [schedHwEditDate, setSchedHwEditDate] = useState('');
  const [schedHwEditHeight, setSchedHwEditHeight] = useState('');
  const [schedHwEditWeight, setSchedHwEditWeight] = useState('');
  const [schedHwEditWeightOz, setSchedHwEditWeightOz] = useState('');
  // Full Schedule collapse: false = hide past (completed/missed/timed_out), true = show all
  const [schedShowPast, setSchedShowPast] = useState(false);
  // Next Well-Check inline H/W editor (Online / Self-Administered types)
  const [nextHwIsEditing, setNextHwIsEditing] = useState(false);
  const [nextHwEditHeight, setNextHwEditHeight] = useState('');
  const [nextHwEditWeight, setNextHwEditWeight] = useState('');
  const [nextHwEditWeightOz, setNextHwEditWeightOz] = useState('');
  // Ref tracks which well-check event triggered a snapshot from the Schedule tab
  // Using a ref avoids stale-closure issues inside handleAssessmentComplete
  const snapshotLinkedEventId = React.useRef<string | null>(null);
  const dispPrescriptionEditorRef = React.useRef<PrescriptionListEditorHandle>(null);
  const dispReferralEditorRef = React.useRef<ReferralListEditorHandle>(null);

  const createWellCheckLocalId = useCallback((): string => {
    const now = Date.now().toString(36);
    const rand = Math.random().toString(36).slice(2, 10);
    return `wc_${now}_${rand}`;
  }, []);

  const getWellCheckTrackActionId = useCallback((): string => {
    const types = actionDef?.actionTypes ?? [];
    const preferredNames = ['Manage Well-Check', 'Manage Well Check', 'Well-Check', 'Well Check'];
    for (const preferredName of preferredNames) {
      const match = types.find(t => (t.name || '').trim().toLowerCase() === preferredName.toLowerCase());
      if (match?.action) return match.action;
    }
    return '1';
  }, [actionDef]);

  const toggleWellCheckTrackAction = useCallback((
    eventId: string,
    eventName: string,
    dueDate: string,
    enabled: boolean
  ) => {
    if (!activeProfile) return;

    const currentWellChecks: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
    const currentActions: AdminActionDto[] = activeProfile.actionsstate ?? [];
    const existingWellCheck = currentWellChecks.find(w => w.eventId === eventId);
    const stableId = (existingWellCheck?.id || '').trim() || createWellCheckLocalId();
    const mvar = `wellcheck_${stableId}`;
    const isWellCheckAction = (action: AdminActionDto) => (action.mvar || '').toLowerCase().startsWith('wellcheck_');

    const normalizedWellChecks: WellCheckDto[] = currentWellChecks.map(w => ({
      ...w,
      trackAction: w.eventId === eventId ? enabled : false,
    }));
    const targetIndex = normalizedWellChecks.findIndex(w => w.eventId === eventId);
    const nextWellCheck: WellCheckDto = {
      id: stableId,
      trackAction: enabled,
      eventId,
      date: editDate.trim() || existingWellCheck?.date || '',
      type: editType.trim() || existingWellCheck?.type || '',
      location: editLocation.trim() || existingWellCheck?.location || '',
      outcome: editNotes.trim() || existingWellCheck?.outcome || '',
      status: existingWellCheck?.status || (editDate.trim() ? 'pending' : ''),
    };
    if (targetIndex >= 0) {
      normalizedWellChecks[targetIndex] = { ...normalizedWellChecks[targetIndex], ...nextWellCheck };
    } else {
      normalizedWellChecks.push(nextWellCheck);
    }

    // Keep Action Plan in sync with exactly one tracked well-check (the selected/current next one).
    const withoutTracked = currentActions.filter(a => !isWellCheckAction(a));
    const nextActions = enabled
      ? (() => {
          const maxRank = withoutTracked.reduce((max, action) => {
            const rank = Number(action.rank ?? 0);
            return Number.isFinite(rank) ? Math.max(max, rank) : max;
          }, 0);
          const newAction: AdminActionDto = {
            source: eventName || 'Well-Check',
            group: '4',
            mvar,
            rank: String(maxRank + 1),
            action: getWellCheckTrackActionId(),
            // For well-check tracking, Action Plan dueDate must always mirror the scheduled
            // appointment date (or remain blank when unscheduled).
            dueDate: editDate.trim(),
            location: editLocation.trim() || existingWellCheck?.location || '',
            schedule: '-1',
            actual: '',
            expense: '',
            importance: '',
            outcome: '',
            created: new Date().toISOString(),
            completed: '',
            status: '1',
          };
          return [...withoutTracked, newAction];
        })()
      : withoutTracked;

    updateProfile(activeProfile.id, {
      wellchecks: normalizedWellChecks,
      actionsstate: nextActions,
    });
    markProfileDirty(activeProfile.id, `${enabled ? 'Enabled' : 'Disabled'} well-check action plan tracking: ${eventId}`);
  }, [
    activeProfile,
    createWellCheckLocalId,
    editDate,
    editLocation,
    editNotes,
    editType,
    getWellCheckTrackActionId,
    markProfileDirty,
    updateProfile,
  ]);

  const selectedTab = WELLVISIT_TABS[selectedTabIndex];

  const handlePrevTab = () => { if (selectedTabIndex > 0) setSelectedTabIndex(selectedTabIndex - 1); };
  const handleNextTab = () => { if (selectedTabIndex < WELLVISIT_TABS.length - 1) setSelectedTabIndex(selectedTabIndex + 1); };

  // Local debug settings that override props (loaded from storage)
  const [localShowDebugInfo, setLocalShowDebugInfo] = useState(showDebugInfo);
  const [localEnableEditPageLogic, setLocalEnableEditPageLogic] = useState(enableEditPageLogic);
  const [localEnableEditCalcLogic, setLocalEnableEditCalcLogic] = useState(enableEditCalcLogic);
  const [localEnableEditReportLogic, setLocalEnableEditReportLogic] = useState(enableEditReportLogic);

  // Create reusable function to load assessments - returns definitions to avoid stale closure issue
  const loadAvailableAssessments = async (): Promise<AdminAssessmentDto[]> => {
    if (!storage || !profileId) return [];
    
    try {
      const adminStateStorage = new AdminStateStorage(storage);
      
      // Load assessment definitions from AdminAssessmentStorage
      const assessmentStorage = new AdminAssessmentStorage(storage);
      let definitions = await assessmentStorage.getAvailableAssessments();

      // Fallback: if no definitions are stored but the ContentStore has downloaded
      // AdminAssessment records (e.g., persisted from a prior session before the bundle
      // extraction path was wired up), re-extract them now.
      if (definitions.length === 0 && useContentStore) {
        console.log('📋 No assessment definitions found — attempting to repopulate from ContentStore...');
        const contentStoreState = useContentStore.getState();
        const extracted = await assessmentStorage.repopulateFromContentStore(contentStoreState);
        if (extracted > 0) {
          definitions = await assessmentStorage.getAvailableAssessments();
          console.log(`📋 Repopulated ${extracted} definition(s) from ContentStore`);
        }
      }

      setAssessmentDefinitions(definitions);
      
      // Check for existing AdminStates that are incomplete (status 'IN_PROGRESS' or numeric < 5)
      const assessmentIds = definitions.map((d: AdminAssessmentDto) => d.sourceId); // Use sourceId — matches how AdminState is stored
      const incompleteStates: AdminStateDto[] = [];
      const seenSourceIds = new Set<string>(); // Deduplicate by sourceId — recId is blank until backend confirms
      
      for (const assessmentId of assessmentIds) {
        const state = await adminStateStorage.loadAdminState(profileId, assessmentId);
        if (state) {
          const isIncomplete = state.status === 'IN_PROGRESS' || 
                             (!isNaN(parseInt(state.status || '1')) && parseInt(state.status || '1') < 5);
          
          // Deduplicate by sourceId (not recId — recId is '' for all in-progress states before backend confirmation)
          const dedupeKey = state.sourceId || assessmentId;
          if (isIncomplete && !seenSourceIds.has(dedupeKey)) {
            incompleteStates.push(state);
            seenSourceIds.add(dedupeKey);
          }
        }
      }
      
      setAvailableAssessments(incompleteStates);
      console.log('📋 Definitions:', definitions.length, '| In-progress:', incompleteStates.length);
      
      return definitions; // Return definitions so callers can use them without stale closure
    } catch (error) {
      console.error('❌ Failed to load available assessments:', error);
      return [];
    }
  };

  // Load completed assessments from AdminResponseStorage
  // Accepts optional definitions param to avoid stale closure when called right after loadAvailableAssessments
  const loadCompletedAssessments = async (freshDefinitions?: AdminAssessmentDto[]) => {
    if (!storage || !profileId) return;
    
    // Use freshly-loaded definitions if provided, otherwise fall back to state (for standalone calls)
    const definitions = freshDefinitions ?? assessmentDefinitions;
    
    try {
      const adminResponseStorage = new AdminResponseStorage(storage);
      const profileResponses = await adminResponseStorage.getProfileResponses(profileId);
      
      // Sort by recUTM descending (most recent first)
      const sortedResponses = profileResponses.responses.sort((a, b) => {
        const dateA = new Date(a.recUTM || a.recDate || 0);
        const dateB = new Date(b.recUTM || b.recDate || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setCompletedAssessments(sortedResponses);
      console.log('📊 Completed assessments:', sortedResponses.length);
      
    } catch (error) {
      console.error('❌ Failed to load completed assessments:', error);
      setCompletedAssessments([]);
    }
  };

  // Consolidated assessment loading effect - prevents race conditions by coordinating all loading
  const loadProviderRequests = async () => {
    if (!storage || !accountId || !profileId) return;
    try {
      const launchStorage = new AdminLaunchRequestStorage(storage);
      const requests = await launchStorage.getRequestsForProfile(accountId, profileId);
      setProviderRequests(requests);
      console.log('📋 WellVisitView: Loaded provider requests:', requests.length);
    } catch (error) {
      console.error('❌ WellVisitView: Failed to load provider requests:', error);
    }
  };

  useEffect(() => {
    let isMemoryCleared = false;
    
    const loadAllAssessmentData = async () => {
      if (!storage || !profileId || isMemoryCleared) return;

      // Check visibility at load time (not as dependency)
      if (showAssessment || showReport) return;

      try {
        const freshDefinitions = await loadAvailableAssessments();
        if (isMemoryCleared) return;
        await loadCompletedAssessments(freshDefinitions);
        await loadProviderRequests();
      } catch (error) {
        console.error('❌ WellVisitView: Failed to load assessment data:', error);
      }
    };

    if (storage && profileId) {
      loadAllAssessmentData();
    }

    return () => { isMemoryCleared = true; };
  }, [storage, accountId, profileId]); // Only trigger on mount and when storage/profile changes

  // Refresh data when returning to WellVisitView from ReportView or AssessmentView  
  useEffect(() => {
    // Track if we just became visible (went from showing assessment/report to not showing)
    if (!showAssessment && !showReport && storage && profileId) {
      // Check if we have existing data - if so, this might be a return from report/assessment
      if (assessmentDefinitions.length > 0 || completedAssessments.length > 0) {
        loadCompletedAssessments();
      }
    }
  }, [showAssessment, showReport]); // Only trigger on visibility changes

  // Refresh completed assessments whenever the History tab becomes active
  useEffect(() => {
    if (selectedTab?.key === 'History' && storage && profileId) {
      loadCompletedAssessments();
    }
  }, [selectedTabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate inline Snapshot Report HTML whenever the snapshot assessment or its definition changes.
  // Uses the same generateFullReportHtml utility as ReportView, so scripts run identically.
  useEffect(() => {
    const generateSnapshotReport = async () => {
      // Find the most recent non-closed well-check snapshot response (sourceId '4')
      const wellCheckResponse = completedAssessments.find(a => {
        const def = assessmentDefinitions.find(d => d.surveyId === a.recSurvey || d.sourceId === a.recSurvey);
        return (def?.sourceId === '4' || a.recSurvey === '4') && a.recStatus !== 'closed';
      });
      if (!wellCheckResponse) {
        setSnapshotReportHtml(null);
        setSnapshotReportWebViewHeight(0);
        return;
      }
      const def = assessmentDefinitions.find(
        d => d.surveyId === wellCheckResponse.recSurvey || d.sourceId === wellCheckResponse.recSurvey
      );
      if (!def?.reports?.length) {
        setSnapshotReportHtml(null);
        return;
      }
      // Load persisted report script edits from storage (same key as ReportView)
      let editedScripts: Record<string, string> = {};
      if (storage && def.surveyId) {
        try {
          const raw = await storage.getItem(`report_script_edits_${def.surveyId}`);
          if (raw) {
            const stored: Record<string, { script: string; dirty: boolean }> = JSON.parse(raw);
            Object.entries(stored).forEach(([id, entry]) => {
              editedScripts[id] = entry.script;
            });
          }
        } catch {
          // Ignore — fall back to default scripts
        }
      }
      // Concatenate all reports (typically one for a well-check)
      const parts: string[] = [];
      for (const report of def.reports) {
        parts.push(generateFullReportHtml(report, wellCheckResponse, profilePHI, editedScripts));
      }
      setSnapshotReportHtml(parts.join('\n'));
      setSnapshotReportWebViewHeight(0); // Reset so WebView posts a fresh height message
    };
    generateSnapshotReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedAssessments, assessmentDefinitions, storage]);

  // ── Load WellCheck schedule from storage ──────────────────────────────────
  useEffect(() => {
    const loadSchedule = async () => {
      if (!storage) return;
      try {
        const selectedId = await storage.getItem('wellcheck_selected_schedule_id');
        const wcs = new WellCheckStorage(storage);
        const lists = await wcs.getAllWellCheckDefs();
        const allSchedules = lists.flatMap(l => l.schedules);
        const match = selectedId
          ? allSchedules.find(s => s.scheduleId === selectedId)
          : allSchedules[0] ?? null;
        setWellCheckSchedule(match ?? null);
        const matchedDef = lists.find(l => l.schedules.some(s => s.scheduleId === match?.scheduleId));
        setWellCheckVaccines(matchedDef?.vaccines ?? []);
        const savedVaccine = await storage.getItem('wellcheck_vaccine_info_enabled');
        if (savedVaccine !== null) setVaccineInfoEnabled(savedVaccine === 'true');
        const savedTrackRx = await storage.getItem('wellcheck_track_prescriptions');
        if (savedTrackRx !== null) setTrackPrescriptions(savedTrackRx === 'true');
        const savedTrackRef = await storage.getItem('wellcheck_track_referrals');
        if (savedTrackRef !== null) setTrackReferrals(savedTrackRef === 'true');
        const savedTrackHW = await storage.getItem('wellcheck_track_height_weight');
        if (savedTrackHW !== null) setTrackHeightWeight(savedTrackHW === 'true');
        const savedMeasUnits = await storage.getItem('wellcheck_measurement_units');
        if (savedMeasUnits) setMeasurementUnits(savedMeasUnits as 'english' | 'metric');

        const actionDefStorage = new ActionDefStorage(storage);
        const loadedActionDef = await actionDefStorage.getActionDef();
        setActionDef(loadedActionDef);
      } catch (e) {
        console.warn('WellVisitView: failed to load well-check schedule', e);
      }
    };
    loadSchedule();
  }, [storage]);

  // ── Sync edit fields when schedule or profile wellchecks change ───────────
  const syncEditFields = useCallback(() => {
    if (!wellCheckSchedule) return;
    const dob = activeProfile?.dob;
    const wellchecks: WellCheckDto[] = (activeProfile?.wellchecks ?? []) as WellCheckDto[];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const events = wellCheckSchedule.events ?? [];
    // Find next upcoming: first event (by days asc) that is not completed and due >= today
    // Sort by days ascending
    const sorted = [...events].sort((a, b) => parseInt(a.days, 10) - parseInt(b.days, 10));
    const computeDue = (days: string): Date | null => {
      if (!dob) return null;
      return new Date(new Date(dob).getTime() + parseInt(days, 10) * 24 * 60 * 60 * 1000);
    };
    const TERM = ['completed', 'missed', 'timed_out'];
    let upcoming = sorted.find(ev => {
      const dto = wellchecks.find(w => w.eventId === ev.eventId);
      if (TERM.includes(dto?.status ?? '')) return false;
      const due = computeDue(ev.days);
      return due ? due >= today : true;
    });
    // Fallback: first non-terminal event regardless of date
    if (!upcoming) upcoming = sorted.find(ev => {
      const dto = wellchecks.find(w => w.eventId === ev.eventId);
      return !TERM.includes(dto?.status ?? '');
    });
    if (upcoming) {
      const dto = wellchecks.find(w => w.eventId === upcoming!.eventId);
      const due = computeDue(upcoming.days);
      const dueStr = due ? due.toISOString().slice(0, 10) : '';
      // Leave editDate blank when no date has been saved — keeps status as "Need to Schedule".
      // The due date is stored separately to seed the picker when the user first opens it.
      setEditDate(dto?.date ?? '');
      setEditDateDue(dueStr);
      setEditLocation(dto?.location ?? '');
      setEditType(dto?.type ?? 'In-Office');
      setEditNotes(dto?.outcome ?? '');
    }
    // Reset / pre-populate ephemeral disposition fields whenever the upcoming event changes.
    // H/W is seeded from the most recent GrowthTrackDto entry so subsequent visits
    // don't start at zero, but the values can still be edited before submitting.
    setDispVaccinesChecked(new Set());
    const growthData = (activeProfile?.growthtrack?.data ?? []) as Array<{ height: string; weight: string; date: string }>;
    const lastGT = growthData.length > 0
      ? [...growthData].filter(d => d.date).sort((a, b) => b.date.localeCompare(a.date))[0]
      : null;
    if (lastGT && trackHeightWeight) {
      setDispHeight(lastGT.height || '');
      if (measurementUnits === 'english') {
        const wNum = parseFloat(lastGT.weight) || 0;
        const wholeLbs = Math.floor(wNum);
        const fracOz = Math.round((wNum - wholeLbs) * 16 * 10) / 10;
        setDispWeight(fracOz > 0 ? String(wholeLbs) : (lastGT.weight || ''));
        setDispWeightOz(fracOz > 0 ? String(fracOz) : '');
      } else {
        setDispWeight(lastGT.weight || '');
        setDispWeightOz('');
      }
    } else {
      setDispHeight('');
      setDispWeight('');
      setDispWeightOz('');
    }
    setDispPrescriptionsEnabled(false);
    setDispReferralsEnabled(false);
    setDispOutcomeAnswer('');
  }, [wellCheckSchedule, activeProfile?.dob, activeProfile?.wellchecks,
      activeProfile?.growthtrack, trackHeightWeight, measurementUnits]);

  useEffect(() => { syncEditFields(); }, [syncEditFields]);

  // Ensure Next Well-Check tracking defaults to enabled and source label stays human-readable.
  // This repairs legacy actions where source was stored as the internal mvar key.
  useEffect(() => {
    if (!activeProfile || !wellCheckSchedule) return;

    const events = [...(wellCheckSchedule.events ?? [])].sort(
      (a, b) => parseInt(a.days, 10) - parseInt(b.days, 10)
    );
    if (events.length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dob = activeProfile.dob;
    const computeDue = (days: string): Date | null => {
      if (!dob) return null;
      return new Date(new Date(dob).getTime() + parseInt(days, 10) * 24 * 60 * 60 * 1000);
    };

    const wellchecks: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
    const TERM_STATUSES = ['completed', 'missed', 'timed_out'];
    const eventRows = events.map(event => {
      const dueDate = computeDue(event.days);
      const dto = wellchecks.find(w => w.eventId === event.eventId) ?? null;
      const isCompleted = TERM_STATUSES.includes(dto?.status ?? '');
      return { event, dueDate, dto, isCompleted };
    });

    let upcomingRow = eventRows.find(r => !r.isCompleted && (!r.dueDate || r.dueDate >= today));
    if (!upcomingRow) upcomingRow = eventRows.find(r => !r.isCompleted);
    if (!upcomingRow) return;

    const { event, dueDate, dto } = upcomingRow;
    const trackingExplicitlyDisabled = dto?.trackAction === false;
    if (trackingExplicitlyDisabled) return;
    const existingId = (dto?.id || '').trim();
    const mvar = existingId ? `wellcheck_${existingId}` : '';
    const existingTracked = existingId
      ? (activeProfile.actionsstate ?? []).find((a: AdminActionDto) => (a.mvar || '').toLowerCase() === mvar.toLowerCase())
      : undefined;
    const hasStaleWellCheckActions = (activeProfile.actionsstate ?? []).some((a: AdminActionDto) => {
      const mv = (a.mvar || '').toLowerCase();
      return mv.startsWith('wellcheck_') && mv !== mvar.toLowerCase();
    });

    const source = (existingTracked?.source || '').trim();
    const sourceLooksFallback = !!existingTracked && (
      source === '' ||
      source.toLowerCase() === mvar.toLowerCase() ||
      source.toLowerCase().startsWith('wellcheck_')
    );

    // Default-checked: create tracked action when missing; also repair fallback source labels.
    if (!existingTracked || sourceLooksFallback || !existingId || hasStaleWellCheckActions) {
      toggleWellCheckTrackAction(
        event.eventId,
        (event.name || '').trim() || 'Well-Check',
        dueDate ? dueDate.toISOString().slice(0, 10) : '',
        true,
      );
    }
  }, [activeProfile, wellCheckSchedule, toggleWellCheckTrackAction]);

  // ── Auto-timeout: mark events as timed_out if disposition window has passed ──
  useEffect(() => {
    if (!activeProfile || !wellCheckSchedule) return;
    const wellchecks: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const TERM = ['completed', 'missed', 'timed_out'];
    let needsUpdate = false;
    const updated = wellchecks.map(dto => {
      if (TERM.includes(dto.status) || !dto.date) return dto;
      const apptDate = new Date(dto.date);
      if (isNaN(apptDate.getTime())) return dto;
      apptDate.setHours(0, 0, 0, 0);
      const dispositionEnd = new Date(apptDate.getTime() + 14 * 24 * 60 * 60 * 1000);
      if (today > dispositionEnd) {
        needsUpdate = true;
        console.log(`⏰ WellVisitView: auto-timing-out event ${dto.eventId}`);
        return { ...dto, status: 'timed_out' };
      }
      return dto;
    });
    if (needsUpdate) {
      updateProfile(activeProfile.id, { wellchecks: updated });
      markProfileDirty(activeProfile.id, 'WellCheck disposition auto-timeout');
    }
  }, [wellCheckSchedule, activeProfile?.id, activeProfile?.wellchecks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save a well-check event to the profile ────────────────────────────────
  // overrideStatus: when provided, sets status explicitly
  // Terminal statuses (completed/missed/timed_out) and intermediate statuses
  // (snapshot_completed/appt_completed) are never downgraded by a plain save.
  const saveWellCheckEvent = useCallback(async (eventId: string, overrideStatus?: string, overrideType?: string, overrideDate?: string) => {
    if (!activeProfile) return;
    setEditSaving(true);
    try {
      const date = (overrideDate ?? editDate).trim();
      const existing: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
      const existingDto = existing.find(w => w.eventId === eventId);
      const TERM = ['completed', 'missed', 'timed_out'];
      const PRESERVE = ['snapshot_completed', 'appt_completed'];
      let status: string;
      if (overrideStatus) {
        status = overrideStatus;
      } else if (TERM.includes(existingDto?.status ?? '') || PRESERVE.includes(existingDto?.status ?? '')) {
        status = existingDto!.status; // preserve current status from a plain field save
      } else {
        status = date ? 'pending' : '';
      }
      const outcome = editNotes.trim() || undefined;
      const type = (overrideType !== undefined ? overrideType : editType).trim() || undefined;
      const updated = existing.filter(w => w.eventId !== eventId);
      const persistedId = existingDto?.id || createWellCheckLocalId();
      updated.push({
        id: persistedId,
        trackAction: existingDto?.trackAction,
        eventId,
        date,
        type,
        location: editLocation.trim(),
        status,
        outcome,
      });

      // Keep the linked well-check action due date and location aligned to the appointment.
      const wellCheckMvar = `wellcheck_${persistedId}`.toLowerCase();
      const currentActions: AdminActionDto[] = activeProfile.actionsstate ?? [];
      const syncedActions = currentActions.map((a) =>
        (a.mvar || '').toLowerCase() === wellCheckMvar
          ? {
              ...a,
              dueDate: date,
              location: editLocation.trim(),
            }
          : a
      );

      updateProfile(activeProfile.id, {
        wellchecks: updated,
        actionsstate: syncedActions,
      });
      markProfileDirty(activeProfile.id, `WellCheck event updated: ${eventId} status=${status}`);
    } catch (e) {
      console.error('WellVisitView: failed to save well-check event', e);
    } finally {
      setEditSaving(false);
    }
  }, [activeProfile, createWellCheckLocalId, editDate, editLocation, editType, editNotes, updateProfile, markProfileDirty]);

  // ── Commit full disposition and close the well-check event ────────────────
  const handleCompleteVisit = useCallback(async (eventId: string, eventVaccines: VaccineSchEvntDto[]) => {
    if (!activeProfile) return;
    setEditSaving(true);
    try {
      const date = editDate.trim();
      const recordDate = date || new Date().toISOString().slice(0, 10);

      // Pre-compute H/W so it can be embedded directly into WellCheckDto below
      const h = dispHeight.trim();
      const wLbs = parseFloat(dispWeight) || 0;
      const wOz  = parseFloat(dispWeightOz) || 0;
      const wCombined = measurementUnits === 'metric' ? dispWeight.trim()
        : wLbs === 0 && wOz === 0 ? ''
        : String(Math.round((wLbs + wOz / 16) * 1000) / 1000);
      const hgtWgtEntry: GrowthDataDto | undefined = trackHeightWeight && (h || wCombined)
        ? { height: h, weight: wCombined, date: recordDate, eventId, ...(wOz > 0 && measurementUnits === 'english' ? { weightOz: dispWeightOz.trim() } : {}) }
        : undefined;

      // 1. Mark WellCheckDto completed with outcome text
      const existingWC: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
      const existingDto = existingWC.find(w => w.eventId === eventId);
      const filteredWC = existingWC.filter(w => w.eventId !== eventId);
      filteredWC.push({
        id: existingDto?.id || createWellCheckLocalId(),
        trackAction: existingDto?.trackAction,
        eventId, date, location: editLocation.trim(), status: 'completed',
        outcome: dispOutcomeAnswer || undefined,
      });

      // 2. Record administered vaccines (only those individually checked)
      const currentVT: VaccineTrackDto = activeProfile.vaccinetrack ?? { events: [] };
      const checkedVaccines = eventVaccines.filter(v => dispVaccinesChecked.has(v.name));
      const newVT: VaccineTrackDto = checkedVaccines.length > 0
        ? { events: [...currentVT.events, ...checkedVaccines.map(v => ({ name: v.name, date: recordDate, eventId: v.eventId }))] }
        : currentVT;

      // 3. Record height/weight measurement in growthtrack, keyed by eventId
      const currentGT: GrowthTrackDto = activeProfile.growthtrack ?? { data: [] };
      const newGT: GrowthTrackDto = hgtWgtEntry
        ? { data: [...(currentGT.data as GrowthDataDto[]).filter(d => d.eventId !== eventId), hgtWgtEntry] }
        : currentGT;

      updateProfile(activeProfile.id, {
        wellchecks: filteredWC,
        vaccinetrack: newVT,
        growthtrack: newGT,
      });
      markProfileDirty(activeProfile.id, `WellCheck visit completed: ${eventId}`);

      // 5. Close the linked snapshot so a new one can be started after this visit.
      //    The most recent non-closed well-check assessment (sourceId=4) is the snapshot
      //    that was completed during the 2-week snapshot window before this appointment.
      if (storage) {
        const snapshotResponse = completedAssessments.find(a => {
          const def = assessmentDefinitions.find(d => d.surveyId === a.recSurvey);
          return def?.sourceId === '4' && a.recStatus !== 'closed';
        });
        if (snapshotResponse) {
          const closedResponse = { ...snapshotResponse, recStatus: 'closed' };
          const adminResponseStorage = new AdminResponseStorage(storage);
          await adminResponseStorage.saveAdminResponse(profileId, closedResponse);
          // Reflect the closure in local state immediately (no full reload needed)
          setCompletedAssessments(prev =>
            prev.map(a => a.recId === snapshotResponse.recId ? closedResponse : a)
          );
          console.log('📋 WellVisitView: Closed snapshot for completed visit:', snapshotResponse.recId);
        }
      }

      // Reset ephemeral fields
      setDispVaccinesChecked(new Set());
      setDispHeight('');
      setDispWeight('');
      setDispWeightOz('');
      setDispPrescriptionsEnabled(false);
      setDispReferralsEnabled(false);
      setDispOutcomeAnswer('');
      setEditNotes('');
    } catch (e) {
      console.error('WellVisitView: failed to complete well-check visit', e);
    } finally {
      setEditSaving(false);
    }
  }, [activeProfile, createWellCheckLocalId, editDate, editLocation, trackHeightWeight, measurementUnits,
      dispVaccinesChecked, dispHeight, dispWeight, dispWeightOz,
      dispPrescriptionsEnabled, dispReferralsEnabled,
      dispOutcomeAnswer, updateProfile, markProfileDirty,
      storage, profileId, completedAssessments, assessmentDefinitions]);

  // Load debug settings from storage (same keys as DebugView)
  useEffect(() => {
    const loadDebugSettings = async () => {
      if (!storage) return;
      try {
        const debugState = await storage.getItem('debug_show_info');
        if (debugState === 'true') setLocalShowDebugInfo(true);
        
        const editPageLogicState = await storage.getItem('debug_edit_page_logic');
        if (editPageLogicState === 'true') setLocalEnableEditPageLogic(true);
        
        const editCalcLogicState = await storage.getItem('debug_edit_calc_logic');
        if (editCalcLogicState === 'true') setLocalEnableEditCalcLogic(true);
        
        const editReportLogicState = await storage.getItem('debug_edit_report_logic');
        if (editReportLogicState === 'true') setLocalEnableEditReportLogic(true);
      } catch (error) {
        console.error('🎯 WellVisitView: Failed to load debug settings:', error);
      }
    };
    
    loadDebugSettings();
  }, [storage]);

  const handleStartAssessment = async (segmentListParam?: string, isContinue: boolean = false) => {
    const targetSegmentList = segmentListParam || '1987'; // Default to PHQ-9
    
    if (!storage) {
      console.error('❌ Storage not available for AdminState management');
      return;
    }
    
    try {
      // Get assessment definition from AdminAssessmentStorage
      const assessmentStorage = new AdminAssessmentStorage(storage);
      const assessmentDefinition = assessmentDefinitions.find(def => def.surveybList === targetSegmentList);
      
      if (!assessmentDefinition) {
        console.error('❌ Assessment definition not found for segmentList:', targetSegmentList);
        return;
      }
      
      // Create AdminStateStorage instance
      const adminStateStorage = new AdminStateStorage(storage);
      
      let existingState: AdminStateDto | null = null;
      
      if (isContinue) {
        // For Continue: Find existing AdminState that exactly matches this assessment definition
        // This should be an AdminState from availableAssessments that matches the sourceId/segmentList
        existingState = availableAssessments.find(state => 
          state.sourceId === assessmentDefinition.sourceId ||
          state.segmentList === assessmentDefinition.surveybList
        ) || null;
        
        console.log('🔍 Continue mode - Found matching AdminState:', existingState ? existingState.recId : 'None');
      } else {
        // For Start New: Always create new AdminState, don't check for existing ones
      }
      
      // Check if we found a valid existing state to continue
      const shouldContinue = existingState && (
        existingState.status === 'IN_PROGRESS' || 
        (!isNaN(parseInt(existingState.status || '1')) && parseInt(existingState.status || '1') < 5)
      );
      
      if (shouldContinue && isContinue && existingState) {
        // Sanitize pageNum: the string 'undefined' can be persisted by data corruption bugs.
        const sanitizedState = (!existingState.pageNum || existingState.pageNum === 'undefined')
          ? { ...existingState, pageNum: '1', nodeIndex: 0, phi: profilePHI ?? existingState.phi }
          : { ...existingState, phi: profilePHI ?? existingState.phi };
        console.log('📋 Found existing AdminState, continuing from page:', sanitizedState.pageNum, 'segment:', sanitizedState.segment);
        console.log('📋 AdminState FULL details:', {
          recId: sanitizedState.recId,
          pageNum: sanitizedState.pageNum,
          segment: sanitizedState.segment,
          status: sanitizedState.status,
          surveyId: sanitizedState.surveyId,
          sourceId: sanitizedState.sourceId,
          backTableLength: sanitizedState.backTable?.length || 0,
          dataLength: sanitizedState.data?.length || 0,
          prefillLength: sanitizedState.prefill?.length || 0,
          wasSanitized: sanitizedState !== existingState
        });
        setCurrentAdminState(sanitizedState);
      } else {
        // Create new AdminState using assessment definition properties
        let newState = await adminStateStorage.createAdminState(
          accountId,
          profileId,
          assessmentDefinition.surveyId,
          assessmentDefinition.sourceId,
          assessmentDefinition.surveybList, // Full segment list from definition
          '1', // Language type ID
          assessmentDefinition.dName, // Assessment name for display
          undefined, // surveyAliasId - not available in WellVisitView, will default to sourceId
          profilePHI
        );

        // ── Well-Check Snapshot (sourceId=4): pre-populate AdminStateDto.data ──────
        // Seed the new assessment with the profile's current milestone answers,
        // per-domain developmental results, and selected concern actions so that
        // the assessment can reference them via putData/getData in its PreScript.
        if (assessmentDefinition.sourceId === '4' && activeProfile) {
          const preData: AdminDataDto[] = [...(newState.data ?? [])];
          const addItem = (name: string, value: string | number | null | undefined) => {
            if (value !== null && value !== undefined && String(value) !== '') {
              preData.push({ name, value: String(value), segment: '', pageNum: '' });
            }
          };

          // Physical age in months
          addItem('agephysical', activeProfile.agephysical);

          // Per-domain developmental results — mvar names use CDC backend naming convention
          const devDomainCodeMap: Record<string, string> = {
            communication:   'lc',
            motor:           'mt',
            finemotor:       'fm',
            grossmotor:      'gm',
            cognitive:       'cg',
            socialemotional: 'se',
          };
          const devDomains = [
            'communication', 'motor', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional',
          ] as const;
          for (const domain of devDomains) {
            const code = devDomainCodeMap[domain];
            addItem(`cdc_${code}_score`,       activeProfile.devage?.[domain]);
            addItem(`cdc_${code}_delay`,       activeProfile.devdelay?.[domain]);
            addItem(`cdc_${code}_result`,      activeProfile.devresult?.[domain]);
            addItem(`cdc_${code}_resultscore`, activeProfile.devresultscore?.[domain]);
          }

          // Milestone states — store as dataValue (1=Yes, 2=Sometimes, 3=Not-Yet) so that
          // the assessment PreScript can compare against the same numeric values used by
          // the milestone question answer set.
          const milestoneStateToDataValue = (state: string): number =>
            state === 'yes' ? 1 : state === 'sometimes' ? 2 : 3;

          // For Well-Check snapshot prefill, include only milestone answers that fall within
          // the current per-domain devFloor/devCeiling window from the active profile.
          const normalizeProfileDomainKey = (rawDomain: string): string | null => {
            const normalized = (rawDomain || '').replace(/[\s_-]/g, '').toLowerCase();
            if (normalized === 'communication') return 'communication';
            if (normalized === 'motor' || normalized === 'motorskills') return 'motor';
            if (normalized === 'finemotor') return 'finemotor';
            if (normalized === 'grossmotor') return 'grossmotor';
            if (normalized === 'cognitive') return 'cognitive';
            if (normalized === 'socialemotional') return 'socialemotional';
            return null;
          };

          const globalStorage = (globalThis as any).__childNavStorage;
          const milestoneStorageForFilter = globalStorage
            ? createMasterMilestoneStorage(globalStorage)
            : null;
          const domainOrderIndexCache = new Map<string, Map<string, number>>();

          const isMilestoneInCurrentWindow = async (mvarid: string): Promise<boolean> => {
            if (!milestoneStorageForFilter) return true;

            try {
              const milestone = await milestoneStorageForFilter.getMilestoneByMvar(mvarid);
              if (!milestone?.domain) return true;

              const profileDomainKey = normalizeProfileDomainKey(milestone.domain);
              if (!profileDomainKey) return true;

              const floorRaw = Number((activeProfile.devfloor as any)?.[profileDomainKey]);
              const ceilingRaw = Number((activeProfile.devceiling as any)?.[profileDomainKey]);
              if (!Number.isFinite(floorRaw) || !Number.isFinite(ceilingRaw)) return true;

              let mvarToOrderIndex = domainOrderIndexCache.get(milestone.domain);
              if (!mvarToOrderIndex) {
                const domainMilestones = await milestoneStorageForFilter.getMilestonesByDomain(milestone.domain);
                const ordered = [...domainMilestones].sort((a, b) => {
                  const ageDiff = (a.itemage ?? 0) - (b.itemage ?? 0);
                  if (ageDiff !== 0) return ageDiff;
                  return (a.seqnum ?? 0) - (b.seqnum ?? 0);
                });
                mvarToOrderIndex = new Map<string, number>();
                for (let i = 0; i < ordered.length; i++) {
                  mvarToOrderIndex.set(ordered[i].mvar, i);
                }
                domainOrderIndexCache.set(milestone.domain, mvarToOrderIndex);
              }

              const orderIndex = mvarToOrderIndex.get(mvarid);
              if (orderIndex === undefined) return true;

              const lower = Math.max(0, Math.trunc(Math.min(floorRaw, ceilingRaw)));
              const upper = Math.max(lower, Math.trunc(Math.max(floorRaw, ceilingRaw)));
              return orderIndex >= lower && orderIndex <= upper;
            } catch {
              // Preserve prior behavior if filtering cannot be resolved.
              return true;
            }
          };

          for (const ms of (activeProfile.milestonestate ?? [])) {
            if (ms.mvarid && ms.state && await isMilestoneInCurrentWindow(ms.mvarid)) {
              addItem(ms.mvarid, milestoneStateToDataValue(ms.state));
            }
          }

          // Concern states with a selected follow-up action (1=Track, 2=Manage, 3=Seek Help)
          for (const cs of (activeProfile.concernsstate ?? [])) {
            if (cs.mvarid && cs.action && cs.action >= 1 && cs.action <= 3) {
              addItem(cs.mvarid, cs.action);
            }
          }

          // Action Plan — flatten the full actionsstate array into a single JSON string
          // so the assessment PreScript can parse it via getData('actionplan').
          const actionsState = (activeProfile as any).actionsstate as import('../../types/api/appProfile').AdminActionDto[] | undefined;
          if (actionsState && actionsState.length > 0) {
            preData.push({ name: 'actionplan', value: JSON.stringify(actionsState), segment: '', pageNum: '' });
          }

          // Profile PHI — stringified AppProfilePHIDto so the backend data service can
          // access identity fields (dname, dob, gender, extras, etc.) alongside assessment data.
          if (profilePHI) {
            preData.push({ name: 'profilephi', value: JSON.stringify(profilePHI), segment: '', pageNum: '' });
            for (const extra of profilePHI.extra ?? []) {
              addItem(extra.name, extra.value);
            }
          }

          // Snapshot execution context flag — mirrors app premium mode in script-friendly form.
          addItem('appm_cdcx', appConfig.isPremium() ? 1 : 0);
          //addItem('appm_cdcx', activeProfile?.isPremium ?? appConfig.isPremium() ? 1 : 0);

          // Well-Check Event ID — the WellCheckSchEvntDto.EventId of the Next Well-Check card
          // that triggered this snapshot (set by Schedule tab when navigating here). Used by
          // the snapshot report to label the associated well-check visit name.
          if (snapshotLinkedEventId.current) {
            preData.push({ name: 'wellcheckeventid', value: snapshotLinkedEventId.current, segment: '', pageNum: '' });
          }

          newState = { ...newState, data: preData };
          await adminStateStorage.saveAdminState(newState);
        }
        // ─────────────────────────────────────────────────────────────────────────

        setCurrentAdminState(newState);
      }
      
      // Set current assessment and show the assessment view
      // When continuing, start from the saved segment; when starting fresh, use the first segment
      const primarySegment = (shouldContinue && existingState?.segment)
        ? existingState.segment
        : assessmentDefinition.surveybList.split(',')[0];
      setCurrentAssessmentId(primarySegment);
      setShowAssessment(true);
      
    } catch (error) {
      console.error('❌ Failed to initialize assessment:', error);
    }
  };

  const handleStartProviderRequest = async (request: AdminLaunchRequestDto) => {
    if (!storage) return;
    const def = request.assessment;
    try {
      const adminStateStorage = new AdminStateStorage(storage);
      const newState = await adminStateStorage.createAdminState(
        accountId,
        profileId,
        def.surveyId,
        def.sourceId,
        def.surveybList,
        '1',
        def.dName,
        undefined,
        profilePHI
      );
      // Stamp the launchqueueId so the backend can correlate the response
      newState.launchqueueId = request.launchqueueId;
      await adminStateStorage.saveAdminState(newState);
      // Ensure the definition is available for report lookup after completion
      setAssessmentDefinitions(prev =>
        prev.find(d => d.surveyId === def.surveyId) ? prev : [...prev, def]
      );
      setCurrentAdminState(newState);
      setCurrentAssessmentId(def.surveybList.split(',')[0]);
      setShowAssessment(true);
    } catch (error) {
      console.error('❌ Failed to start provider request assessment:', error);
    }
  };

  const handleAssessmentComplete = (adminState: AdminStateDto) => {
    console.log('Assessment completed with AdminState:', adminState);
    setShowAssessment(false);
    setCurrentAdminState(null);
    // If this assessment was initiated from a Schedule well-check event, mark it as snapshot_completed
    if (snapshotLinkedEventId.current) {
      const linkedId = snapshotLinkedEventId.current;
      snapshotLinkedEventId.current = null;
      console.log('📋 WellVisitView: Linking completed assessment to well-check event:', linkedId);
      saveWellCheckEvent(linkedId, 'snapshot_completed');
    }
    // Reload assessments to reflect the completion
    loadAvailableAssessments();
    loadCompletedAssessments(); // Refresh completed assessments
  };

  const handleAssessmentCancel = () => {
    setShowAssessment(false);
    setCurrentAssessmentId(null);
    setCurrentAdminState(null);
    // Reload assessments using the shared function
    loadAvailableAssessments();
  };

  // Handle showing a completed assessment report
  const handleShowReport = (recId: string) => {
    console.log('📊 Showing report for RecId:', recId);
    console.log('📊 PHI trace:', { name: activeProfile?.name, dob: activeProfile?.dob, gender: activeProfile?.gender, hasPHI });
    
    // Find the assessment response to get the recSurvey
    const assessment = completedAssessments.find(a => a.recId === recId);
    
    if (assessment) {
      // recSurvey stores adminState.sourceId — match against both surveyId and sourceId
      const matchedDefinition = assessmentDefinitions.find(
        def => def.surveyId === assessment.recSurvey || def.sourceId === assessment.recSurvey
      );
      console.log('📊 handleShowReport lookup:', {
        recSurvey: assessment.recSurvey,
        recAlias: assessment.recAlias,
        definitionsAvailable: assessmentDefinitions.map(d => ({ surveyId: d.surveyId, sourceId: d.sourceId, dName: d.dName })),
        matchedDefinition: matchedDefinition ? { surveyId: matchedDefinition.surveyId, sourceId: matchedDefinition.sourceId, dName: matchedDefinition.dName } : 'NOT FOUND',
      });

      setSelectedAssessmentDefinition(matchedDefinition || null);
    } else {
      console.warn('⚠️ Assessment not found for RecId:', recId);
      setSelectedAssessmentDefinition(null);
    }
    
    setSelectedReportRecId(recId);
    setShowReport(true);
  };

  // Handle going back from report view
  const handleReportBack = () => {
    setShowReport(false);
    setSelectedReportRecId(null);
    setSelectedAssessmentDefinition(null);
  };

  // Show ReportView if report is requested
  if (showReport && selectedReportRecId) {
    return (
      <ReportView
        profileId={profileId}
        recId={selectedReportRecId}
        assessmentDefinition={selectedAssessmentDefinition || undefined}
        storage={storage}
        apiService={apiService}
        showDebugInfo={localShowDebugInfo}
        enableEditReportLogic={localEnableEditReportLogic}
        profilePHI={profilePHI}
        profileConnections={activeProfile?.connections ?? []}
        onBack={handleReportBack}
        onViewPdf={onViewPdf}
        accountId={accountId}
      />
    );
  }

  if (showAssessment && (useContentStore || contentService)) {
    return (
      <AdminView
        accountId={accountId}
        profileId={profileId}
        assessmentId={currentAssessmentId || '1987'}
        languageId="1"
        onComplete={handleAssessmentComplete}
        onCancel={handleAssessmentCancel}
        useContentStore={useContentStore}
        apiService={apiService}
        contentService={contentService}
        storage={storage}
        showDebugInfo={localShowDebugInfo}
        enableEditPageLogic={localEnableEditPageLogic}
        skipContentAutoLoad={!!currentAdminState}
        initialAdminState={currentAdminState}
      />
    );
  }
  const renderRequestsContent = () => (
    <ScrollView style={styles.contentContainer}>

      {/* Provider Requests */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🏥 Provider Requests</Text>
        <View style={styles.checklistCard}>
          {providerRequests.length > 0 ? (
            <>
              <Text style={styles.assessmentDescription}>
                Your provider has requested the following assessments. Tap Start to begin.
              </Text>
              {providerRequests.map((req, index) => {
                const def = req.assessment;
                const isDone = req.status?.toLowerCase() === 'completed';
                return (
                  <View key={req.launchqueueId || index}
                    style={[styles.assessmentButton, { marginBottom: 8, opacity: isDone ? 0.55 : 1 }]}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.assessmentButtonText}>{def.dName || 'Assessment'}</Text>
                      {req.dueDate ? (
                        <Text style={styles.assessmentProgress}>Due: {req.dueDate}</Text>
                      ) : null}
                      <Text style={[styles.assessmentProgress,
                        { color: isDone ? '#27ae60' : '#e67e22' }]}>
                        {isDone ? '✓ Completed' : '⏳ Pending'}
                      </Text>
                    </View>
                    {!isDone && (
                      <TouchableOpacity
                        style={[styles.newAssessmentButton, { marginBottom: 0, marginLeft: 8, alignSelf: 'center' }]}
                        onPress={() => handleStartProviderRequest(req)}
                      >
                        <Text style={styles.assessmentButtonText}>▶ Start</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                );
              })}
            </>
          ) : (
            <View style={styles.noCompletedContainer}>
              <Text style={styles.noCompletedText}>No provider requests at this time.</Text>
            </View>
          )}
        </View>
      </View>

      {/* In-Progress Assessments (only shown when some exist) */}
      {availableAssessments.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>📋 In-Progress Assessments</Text>
          <View style={styles.checklistCard}>
            <Text style={styles.assessmentDescription}>
              Continue an assessment you have already started.
            </Text>
            {availableAssessments.map((state, index) => {
              const matchedDef = assessmentDefinitions.find(
                d => d.sourceId === state.sourceId || d.surveyId === state.surveyId
              );
              const displayName = state.dName || matchedDef?.dName || `Assessment ${state.sourceId}`;
              const progress = state.pageNum || '1';
              return (
                <TouchableOpacity
                  key={state.recId || index}
                  style={[styles.assessmentButton, { marginBottom: 8, alignSelf: 'center', maxWidth: 450, width: '100%' }]}
                  onPress={() => handleStartAssessment(
                    state.segmentList || matchedDef?.surveybList || state.sourceId,
                    true
                  )}
                >
                  <Text style={styles.assessmentButtonText} numberOfLines={1}>▶ Continue {displayName}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      )}

      {/* Start New Assessment */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📝 Self-Administer</Text>
        {storage ? (
          <View style={styles.checklistCard}>
            <Text style={styles.assessmentDescription}>
              Select an assessment below. A new session will be created and you will be taken directly to the assessment.
            </Text>
            {(() => {
              // Exclude Well-Check Snapshot (sourceId=4) — must be started from Snapshot/Schedule tabs.
              // Exclude any assessment that already has an in-progress state.
              // Match on sourceId only — surveyId defaults to 'ASSESSMENT' for all defs
              // and would false-match every entry when any in-progress state shares that default.
              const inProgressSourceIds = new Set(
                availableAssessments.map(s => s.sourceId).filter(Boolean)
              );
              const notInProgress = (def: AdminAssessmentDto) => !inProgressSourceIds.has(def.sourceId);

              const isPremium = appConfig.isPremium();
              //const isPremium = activeProfile?.isPremium ?? appConfig.isPremium();

              // Profile age in days (undefined dob → 0)
              const profileAgeInDays = (() => {
                const dob = activeProfile?.dob;
                if (!dob) return 0;
                const d = new Date(dob);
                return isNaN(d.getTime()) ? 0 : Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
              })();

              // Non-premium: only Maternal Depression Risk (7) and Adult Behavioral Health (3), in that order.
              // Premium: Autism Risk (8) and ADHD Risk (9) prepended (age > 365 only), then the standard list.
              const STANDARD_SOURCE_IDS = ['7', '3'];
              const PREMIUM_EXTRA_SOURCE_IDS = profileAgeInDays > 365 ? ['8', '9'] : [];

              let allowedSourceIds: string[];
              if (isPremium) {
                allowedSourceIds = [...PREMIUM_EXTRA_SOURCE_IDS, ...STANDARD_SOURCE_IDS];
              } else {
                allowedSourceIds = STANDARD_SOURCE_IDS;
              }

              // Build ordered list: follow allowedSourceIds order, then skip Snapshot (4) + in-progress.
              const selfAdminDefs = allowedSourceIds
                .map(sid => assessmentDefinitions.find(def => def.sourceId === sid))
                .filter((def): def is AdminAssessmentDto => !!def && notInProgress(def));

              return selfAdminDefs.length > 0 ? (
                selfAdminDefs.map((def, index) => (
                  <TouchableOpacity
                    key={`${def.sourceId}_${index}`}
                    style={[styles.newAssessmentButton, { marginBottom: 8, alignSelf: 'center', maxWidth: 450, width: '100%' }]}
                    onPress={() => handleStartAssessment(def.surveybList, false)}
                  >
                    <Text style={styles.assessmentButtonText} numberOfLines={1}>▶ Start {def.dName}</Text>
                  </TouchableOpacity>
                ))
              ) : (
                <View style={styles.noCompletedContainer}>
                  <Text style={styles.noCompletedText}>No assessments available.</Text>
                </View>
              );
            })()}
          </View>
        ) : (
          <View style={styles.checklistCard}>
            <View style={styles.noCompletedContainer}>
              <Text style={styles.noCompletedText}>
                Storage unavailable — assessments cannot be loaded.
              </Text>
            </View>
          </View>
        )}
      </View>

    </ScrollView>
  );

  const renderScheduleContent = () => {
    // ── Derived schedule data ──────────────────────────────────────────────
    const dob = activeProfile?.dob ?? null;
    const wellchecks: WellCheckDto[] = (activeProfile?.wellchecks ?? []) as WellCheckDto[];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const computeDue = (days: string): Date | null => {
      if (!dob) return null;
      const d = parseInt(days, 10);
      return isNaN(d) ? null : new Date(new Date(dob).getTime() + d * 24 * 60 * 60 * 1000);
    };

    const fmtDate = (d: Date | null) =>
      d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date';

    if (!wellCheckSchedule) {
      return (
        <ScrollView style={styles.contentContainer}>
          <View style={styles.section}>
            <View style={styles.noCompletedContainer}>
              <Text style={styles.noCompletedText}>
                No well-check schedule selected.{`\n`}Choose a schedule in Settings to get started.
              </Text>
            </View>
          </View>
        </ScrollView>
      );
    }

    const events = [...(wellCheckSchedule.events ?? [])].sort(
      (a, b) => parseInt(a.days, 10) - parseInt(b.days, 10)
    );

    const TERM_STATUSES = ['completed', 'missed', 'timed_out'];

    // Build enriched event rows
    const eventRows = events.map(event => {
      const dueDate = computeDue(event.days);
      const dto = wellchecks.find(w => w.eventId === event.eventId) ?? null;
      const isPast = dueDate ? dueDate < today : false;
      const isCompleted = TERM_STATUSES.includes(dto?.status ?? '');
      return { event, dueDate, dto, isPast, isCompleted };
    });

    // Next upcoming: first non-terminal event due on or after today
    let upcomingRow = eventRows.find(r => !r.isCompleted && (!r.dueDate || r.dueDate >= today));
    // Fallback: first non-terminal event (even if overdue)
    if (!upcomingRow) upcomingRow = eventRows.find(r => !r.isCompleted);

    const statusColors: Record<string, string> = {
      completed:        '#27ae60',
      missed:           '#e74c3c',
      timed_out:        '#e67e22',
      pending:          '#f39c12',
      appt_completed:   '#3498db',
      snapshot_completed: '#10b981',
    };
    const statusIcon = (dto: WellCheckDto | null, isPast: boolean, isNext: boolean) => {
      if (!dto) return isNext ? '📍' : isPast ? '⚠️' : '○';
      if (dto.status === 'completed')         return '✅';
      if (dto.status === 'missed')            return '❌';
      if (dto.status === 'timed_out')         return '⏰';
      if (dto.status === 'appt_completed')    return '📝';
      if (dto.status === 'snapshot_completed') return '📸';
      return isNext ? '📍' : isPast ? '⚠️' : '⏳';
    };

    return (
      <ScrollView style={styles.contentContainer}>

        {/* ── Card 1: Next Well-Check (Editable) ────────────────────────────── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>📅 Next Well-Check</Text>

          {upcomingRow ? (() => {
            const { event, dueDate, dto } = upcomingRow;

            // Parse typed appointment date for action-button logic
            const apptDateObj = (() => {
              const s = editDate.trim();
              if (!s) return null;
              const d = new Date(s);
              return isNaN(d.getTime()) ? null : d;
            })();
            const snapshotCutoff = apptDateObj
              ? new Date(apptDateObj.getTime() - 14 * 24 * 60 * 60 * 1000)
              : null;
            const dispositionCutoff = apptDateObj
              ? new Date(apptDateObj.getTime() + 14 * 24 * 60 * 60 * 1000)
              : null;
            const isBeforeSnapshot       = snapshotCutoff ? today < snapshotCutoff : true;
            // Snapshot window: [apptDate-14d, apptDate)  — exclusive of apptDate so disposition takes priority
            const isInSnapshotWindow     = !!(snapshotCutoff && apptDateObj && today >= snapshotCutoff && today < apptDateObj);
            // Disposition window: [apptDate, apptDate+14d]
            const isInDispositionWindow  = !!(apptDateObj && today >= apptDateObj && dispositionCutoff && today <= dispositionCutoff);
            const isPastDispositionWindow = !!(dispositionCutoff && today > dispositionCutoff);

            // Well-check snapshot assessment linkage
            const wellCheckDef        = assessmentDefinitions.find(d => d.sourceId === '4');
            const wellCheckInProgress = availableAssessments.find(a =>
              a.sourceId === '4' ||
              assessmentDefinitions.find(d => d.surveyId === a.surveyId)?.sourceId === '4'
            );

            // Computed display status
            const computedStatusLabel = !editDate.trim()
              ? 'Need to Schedule'
              : 'Pending';
            const computedStatusColor = !editDate.trim() ? '#e67e22' : '#3498db';

            return (
              <View style={[styles.appointmentCard, { borderLeftColor: '#3498db' }]}>

                {/* Row 1: event name + due date */}
                <View style={styles.schedHeaderRow}>
                  <Text style={[styles.appointmentTitle, { flex: 1, marginBottom: 0 }]}>{event.name}</Text>
                  <Text style={styles.schedDueLabel}>Due: {fmtDate(dueDate)}</Text>
                </View>

                {/* Row 2: Appointment Date — native date picker */}
                <View style={styles.schedFieldRow}>
                  <Text style={styles.schedInlineLabel}>📅 Appt Date</Text>
                  {Platform.OS === 'web' ? (
                    // @ts-ignore — web-only element
                    <input
                      aria-label="Appointment date"
                      type="date"
                      // When focused and no date is saved, show the due date so the picker
                      // opens at the right month. On blur without selection it reverts to blank.
                      value={editDatePickerFocused && !editDate.trim() ? editDateDue : editDate}
                      onFocus={() => setEditDatePickerFocused(true)}
                      onChange={(e: any) => {
                        setEditDate(e.target.value);
                        setEditDatePickerFocused(false);
                      }}
                      onBlur={() => {
                        setEditDatePickerFocused(false);
                        saveWellCheckEvent(event.eventId);
                      }}
                      style={{
                        width: 160, border: '1px solid #d0d7de', borderRadius: 6,
                        padding: '6px 10px', fontSize: 13, color: '#2c3e50',
                        backgroundColor: '#fafbfc', outline: 'none',
                      }}
                    />
                  ) : (
                    <TouchableOpacity
                      style={[styles.schedInlineDateButton, { maxWidth: 160 }]}
                      onPress={() => setEditDatePickerEventId(event.eventId)}
                    >
                      <Text style={[styles.schedInlineDateButtonText, !editDate.trim() && styles.schedInlineDateButtonPlaceholder]}>
                        {editDate.trim() || editDateDue || 'YYYY-MM-DD'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
                {Platform.OS !== 'web' && (
                  <DatePickerModal
                    visible={editDatePickerEventId === event.eventId}
                    title="Appointment Date"
                    initialValue={editDate.trim() || editDateDue || new Date().toISOString().slice(0, 10)}
                    yearsBack={10}
                    yearsForward={5}
                    accentColor={appConfig.getNavButtonColor()}
                    onCancel={() => setEditDatePickerEventId(null)}
                    onConfirm={(nextDate) => {
                      setEditDate(nextDate);
                      setEditDatePickerEventId(null);
                      saveWellCheckEvent(event.eventId, undefined, undefined, nextDate);
                    }}
                  />
                )}

                {/* Row 2b: Type selector */}
                <View style={styles.schedFieldRow}>
                  <Text style={styles.schedInlineLabel}>🏷️ Type</Text>
                  <View style={styles.schedTypeOptions}>
                    {([
                      { value: 'In-Office', label: 'In-Office' },
                      { value: 'Online', label: 'Online' },
                      { value: 'Self-Administered', label: 'Home' },
                    ] as const).map(opt => {
                      const selected = editType === opt.value;
                      return (
                        <TouchableOpacity
                          key={opt.value}
                          style={[
                            styles.schedTypeOptionChip,
                            {
                              borderColor: selected ? appConfig.getNavButtonColor() : '#d0d7de',
                              backgroundColor: selected ? appConfig.getNavButtonColor() : '#fafbfc',
                            }
                          ]}
                          onPress={() => {
                            const next = editType === opt.value ? 'In-Office' : opt.value;
                            setEditType(next);
                            saveWellCheckEvent(event.eventId, undefined, next);
                          }}
                        >
                          <Text style={[styles.schedTypeOptionText, { color: selected ? '#ffffff' : '#5a6472', fontWeight: selected ? '600' : '400' }]} numberOfLines={1}>
                            {opt.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                {/* Row 3: Location — In-Office only */}
                {editType === 'In-Office' && (
                  <View style={styles.schedFieldRow}>
                    <Text style={styles.schedInlineLabel}>📍 Clinic</Text>
                    <TextInput
                      style={styles.schedInlineInput}
                      value={editLocation}
                      onChangeText={setEditLocation}
                      onBlur={() => saveWellCheckEvent(event.eventId)}
                      placeholder="Enter clinic"
                      placeholderTextColor="#aaa"
                    />
                  </View>
                )}

                {/* Row 5: Progressive action */}
                {apptDateObj && isBeforeSnapshot ? (
                  // > 2 weeks away — snapshot pending row (before status)
                  <View style={styles.schedFieldRow}>
                    <Text style={styles.schedInlineLabel}>📸 Snapshot</Text>
                    <Text style={[styles.schedComputedStatus, { color: '#5b7db1', fontWeight: '500' }]}>
                      Pending {snapshotCutoff ? fmtDate(snapshotCutoff) : ''}
                    </Text>
                  </View>
                ) : null}

                {/* Vaccines row — shown between Snapshot and Status when enabled */}
                {vaccineInfoEnabled && (
                  <View style={styles.schedFieldRow}>
                    <Text style={styles.schedInlineLabel}>💉 Vaccines</Text>
                    <Text style={[styles.schedComputedStatus, { color: (event.vaccines?.length ?? 0) > 0 ? '#27ae60' : '#95a5a6', flex: 1, flexWrap: 'wrap' }]}>
                      {(event.vaccines?.length ?? 0) > 0
                        ? event.vaccines.map(v => v.name).join(', ') + (editType === 'Online' || editType === 'Self-Administered' || editType === 'Home' ? ' needed' : ' expected')
                        : 'None required'}
                    </Text>
                  </View>
                )}

                {/* Row 4: Status — shows action button(s) in the value column when actionable */}
                <View style={styles.schedFieldRow}>
                  <Text style={styles.schedInlineLabel}>🔵 Status</Text>
                  {isInSnapshotWindow ? (
                    // Within 2-week snapshot window — snapshot button replaces status text
                    dto?.status === 'snapshot_completed' ? (
                      <Text style={[styles.schedComputedStatus, { color: '#10b981' }]}>✅ Snapshot Completed</Text>
                    ) : wellCheckInProgress ? (
                      <TouchableOpacity
                        style={styles.schedActionBtn}
                        onPress={() => {
                          snapshotLinkedEventId.current = event.eventId;
                          setSnapshotEventName(event.name);
                          setSelectedTabIndex(1);
                        }}
                      >
                        <Text style={styles.schedActionBtnText}>▶ Continue Snapshot</Text>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={styles.schedActionBtn}
                        onPress={() => {
                          snapshotLinkedEventId.current = event.eventId;
                          setSnapshotEventName(event.name);
                          setSelectedTabIndex(1);
                        }}
                      >
                        <Text style={styles.schedActionBtnText}>▶ Start Snapshot</Text>
                      </TouchableOpacity>
                    )
                  ) : isInDispositionWindow ? (
                    // Disposition window: [apptDate, apptDate+14d]
                    dto?.status === 'completed' ? (
                      <Text style={[styles.schedComputedStatus, { color: '#27ae60' }]}>✅ Visit Completed</Text>
                    ) : dto?.status === 'missed' ? (
                      <Text style={[styles.schedComputedStatus, { color: '#e74c3c' }]}>❌ Missed</Text>
                    ) : dto?.status === 'appt_completed' ? (
                      <View style={styles.schedApptBadge}><Text style={styles.schedApptBadgeText}>🏥 Appt Attended</Text></View>
                    ) : (
                      // Show Appt Attended / Missed buttons
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity
                          style={[styles.schedStatusBtn, { backgroundColor: '#3498db', borderColor: '#3498db' }, editSaving && { opacity: 0.6 }]}
                          disabled={editSaving}
                          onPress={() => saveWellCheckEvent(event.eventId, 'appt_completed')}
                        >
                          <Text style={[styles.schedStatusBtnText, { color: '#fff' }]}>🏥 Appt Attended</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.schedStatusBtn, { backgroundColor: '#e74c3c', borderColor: '#e74c3c' }, editSaving && { opacity: 0.6 }]}
                          disabled={editSaving}
                          onPress={() => saveWellCheckEvent(event.eventId, 'missed')}
                        >
                          <Text style={[styles.schedStatusBtnText, { color: '#fff' }]}>❌ Missed</Text>
                        </TouchableOpacity>
                      </View>
                    )
                  ) : isPastDispositionWindow ? (
                    // Past disposition window — auto-timeout already handled by useEffect
                    <Text style={[styles.schedComputedStatus, { color: '#e67e22' }]}>⏰ Timed Out</Text>
                  ) : (
                    // No action available — show computed status text
                    <Text style={[styles.schedComputedStatus, { color: computedStatusColor }]}>
                      {computedStatusLabel}
                    </Text>
                  )}
                </View>

                {/* Disposition form — shown when Appt Attended is selected */}
                {isInDispositionWindow && dto?.status === 'appt_completed' && (() => {
                  const heightLabel = measurementUnits === 'metric' ? 'cm' : 'in';
                  const weightLabel = measurementUnits === 'metric' ? 'kg' : 'lb';
                  return (
                    <View style={styles.schedDispositionForm}>

                      {/* Vaccines Received — full-width block, header + wrapping checkbox grid */}
                      {vaccineInfoEnabled && (event.vaccines ?? []).length > 0 && (() => {
                        const vaxNames = (event.vaccines ?? []).map(v => v.name);
                        const allChecked = vaxNames.length > 0 && vaxNames.every(v => dispVaccinesChecked.has(v));
                        return (
                          <View style={styles.schedDispVaxSection}>
                            {/* Header row: label + Select All button */}
                            <View style={styles.schedDispVaxHeader}>
                              <Text style={styles.schedDispVaxTitle}>💉 Vaccines Received</Text>
                              <TouchableOpacity
                                style={styles.schedDispSelectAll}
                                onPress={() => setDispVaccinesChecked(allChecked ? new Set() : new Set(vaxNames))}
                              >
                                <Text style={styles.schedDispSelectAllText}>
                                  {allChecked ? 'Deselect All' : 'Select All'}
                                </Text>
                              </TouchableOpacity>
                            </View>
                            {/* Checkbox grid — wraps left-to-right */}
                            <View style={styles.schedDispVaxGrid}>
                              {vaxNames.map((vax) => (
                                <TouchableOpacity
                                  key={vax}
                                  style={styles.schedDispVaxChip}
                                  onPress={() => {
                                    const next = new Set(dispVaccinesChecked);
                                    if (next.has(vax)) next.delete(vax); else next.add(vax);
                                    setDispVaccinesChecked(next);
                                  }}
                                >
                                  <View style={[styles.schedDispCheckbox, dispVaccinesChecked.has(vax) && styles.schedDispCheckboxOn]}>
                                    {dispVaccinesChecked.has(vax) && <Text style={styles.schedDispCheckMark}>✓</Text>}
                                  </View>
                                  <Text style={styles.schedDispVaxName}>{vax}</Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                        );
                      })()}

                      {/* Height / Weight — full-width block matching Vaccines layout */}
                      {trackHeightWeight && (() => {
                        const dob = activeProfile?.dob;
                        const isUnder1Month = dob ? (() => {
                          const d = new Date(dob); const now = new Date();
                          return !isNaN(d.getTime()) && (now.getTime() - d.getTime()) < 30 * 24 * 60 * 60 * 1000;
                        })() : false;
                        const showOz = isUnder1Month && measurementUnits === 'english';
                        // Ensure values typed as integers get a .0 suffix so the display
                        // width stays stable when stepping (prevents left/right flicker).
                        const normalizeHalf = (v: string) =>
                          v === '' ? '' : v.includes('.') ? v : `${v}.0`;
                        const webInputStyle = (w?: number) => ({ width: w ?? 72, border: '1px solid #d0d7de', borderRadius: 6, padding: '6px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', textAlign: 'right' as const });
                        return (
                          <View style={styles.schedDispVaxSection}>
                            <View style={styles.schedDispVaxHeader}>
                              <Text style={styles.schedDispVaxTitle}>📏 Height / Weight</Text>
                            </View>
                            <View style={styles.schedDispVaxGrid}>
                              {/* Height — step 0.5 for English (in), step 1 for metric (cm) */}
                              <View style={styles.schedDispMeasureGroup}>
                                {Platform.OS === 'web' ? (
                                  // @ts-ignore
                                  <input type="number" value={dispHeight}
                                    step={measurementUnits === 'metric' ? '1' : '0.5'}
                                    onChange={(e: any) => setDispHeight(
                                      measurementUnits === 'metric' ? e.target.value : normalizeHalf(e.target.value)
                                    )}
                                    placeholder="0" style={webInputStyle()}
                                  />
                                ) : (
                                  <TextInput style={styles.schedDispMeasureInput}
                                    value={dispHeight} onChangeText={setDispHeight}
                                    placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                )}
                                <Text style={styles.schedDispMeasureUnit}>{heightLabel}</Text>
                              </View>
                              {/* Weight — integer lbs when oz active, else 0.5 steps */}
                              <View style={styles.schedDispMeasureGroup}>
                                {Platform.OS === 'web' ? (
                                  // @ts-ignore
                                  <input type="number" value={dispWeight}
                                    step={showOz ? '1' : '0.5'}
                                    onChange={(e: any) => setDispWeight(
                                      showOz ? e.target.value : normalizeHalf(e.target.value)
                                    )}
                                    placeholder="0" style={webInputStyle()}
                                  />
                                ) : (
                                  <TextInput style={styles.schedDispMeasureInput}
                                    value={dispWeight} onChangeText={setDispWeight}
                                    placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                )}
                                <Text style={styles.schedDispMeasureUnit}>{weightLabel}</Text>
                              </View>
                              {/* oz input — 0.5 steps, child under 1 month + English only */}
                              {showOz && (
                                <View style={styles.schedDispMeasureGroup}>
                                  {Platform.OS === 'web' ? (
                                    // @ts-ignore
                                    <input type="number" value={dispWeightOz}
                                      step="0.5" min="0" max="15.5"
                                      onChange={(e: any) => setDispWeightOz(normalizeHalf(e.target.value))}
                                      placeholder="0" style={webInputStyle(60)}
                                    />
                                  ) : (
                                    <TextInput style={[styles.schedDispMeasureInput, { width: 60 }]}
                                      value={dispWeightOz} onChangeText={setDispWeightOz}
                                      placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                  )}
                                  <Text style={styles.schedDispMeasureUnit}>oz</Text>
                                </View>
                              )}
                            </View>
                          </View>
                        );
                      })()}

                      {/* Prescriptions row */}
                      {trackPrescriptions && editType !== 'Self-Administered' && <View style={styles.schedDispVaxSection}>
                        <View style={[styles.schedDispVaxHeader, { marginBottom: 0 }]}>
                          <TouchableOpacity
                            onPress={() => setDispPrescriptionsEnabled(!dispPrescriptionsEnabled)}
                          >
                            <View style={styles.schedDispCheckRow}>
                              <View style={[styles.schedDispCheckbox, dispPrescriptionsEnabled && styles.schedDispCheckboxOn]}>
                                {dispPrescriptionsEnabled && <Text style={styles.schedDispCheckMark}>✓</Text>}
                              </View>
                              <Text style={styles.schedDispVaxTitle}>📝 Prescriptions to fill</Text>
                            </View>
                          </TouchableOpacity>
                          {dispPrescriptionsEnabled && (
                            <TouchableOpacity onPress={() => dispPrescriptionEditorRef.current?.openAdd()}>
                              <Text style={{ fontSize: 12, color: '#10b981', fontWeight: '500' }}>Add Prescription</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        {dispPrescriptionsEnabled && (
                          <PrescriptionListEditor
                            ref={dispPrescriptionEditorRef}
                            events={activeProfile?.prescriptiontrack?.events ?? []}
                            onUpdate={rxEvents => {
                              if (!activeProfile) return;
                              updateProfile(activeProfile.id, { prescriptiontrack: { events: rxEvents } });
                              markProfileDirty(activeProfile.id, 'Prescriptions updated');
                            }}
                          />
                        )}
                      </View>}

                      {/* Referrals row */}
                      {trackReferrals && editType !== 'Self-Administered' && <View style={styles.schedDispVaxSection}>
                        <View style={[styles.schedDispVaxHeader, { marginBottom: 0 }]}>
                          <TouchableOpacity
                            onPress={() => setDispReferralsEnabled(!dispReferralsEnabled)}
                          >
                            <View style={styles.schedDispCheckRow}>
                              <View style={[styles.schedDispCheckbox, dispReferralsEnabled && styles.schedDispCheckboxOn]}>
                                {dispReferralsEnabled && <Text style={styles.schedDispCheckMark}>✓</Text>}
                              </View>
                              <Text style={styles.schedDispVaxTitle}>👥 Referrals to schedule</Text>
                            </View>
                          </TouchableOpacity>
                          {dispReferralsEnabled && (
                            <TouchableOpacity onPress={() => dispReferralEditorRef.current?.openAdd()}>
                              <Text style={{ fontSize: 12, color: '#3b82f6', fontWeight: '500' }}>Add Referral</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        {dispReferralsEnabled && (
                          <ReferralListEditor
                            ref={dispReferralEditorRef}
                            events={activeProfile?.referraltrack?.events ?? []}
                            onUpdate={refEvents => {
                              if (!activeProfile) return;
                              updateProfile(activeProfile.id, { referraltrack: { events: refEvents } });
                              markProfileDirty(activeProfile.id, 'Referrals updated');
                            }}
                          />
                        )}
                      </View>}

                      {/* Outcome row */}
                      <View style={styles.schedDispVaxSection}>
                        <View style={styles.schedDispCheckRow}>
                          <Text style={styles.schedDispVaxTitle}>💬 Were your concerns and questions adequately addressed?</Text>
                        </View>
                        <View style={[styles.schedDispRadioGroup, { marginTop: 8 }]}>
                          <TouchableOpacity style={styles.schedDispRadioOption} onPress={() => setDispOutcomeAnswer('yes')}>
                            <View style={[styles.schedDispRadio, dispOutcomeAnswer === 'yes' && styles.schedDispRadioOn]} />
                            <Text style={styles.schedDispRadioLabel}>Yes</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.schedDispRadioOption} onPress={() => setDispOutcomeAnswer('no')}>
                            <View style={[styles.schedDispRadio, dispOutcomeAnswer === 'no' && styles.schedDispRadioOn]} />
                            <Text style={styles.schedDispRadioLabel}>No</Text>
                          </TouchableOpacity>
                        </View>
                      </View>

                      {/* Complete Well-Check button */}
                      <View style={{ marginTop: 12, paddingLeft: 8, alignItems: 'flex-start' }}>
                        <TouchableOpacity
                          style={[styles.schedActionBtn, editSaving && { opacity: 0.6 }]}
                          disabled={editSaving}
                          onPress={() => handleCompleteVisit(event.eventId, event.vaccines ?? [])}
                        >
                          <Text style={styles.schedActionBtnText}>✅ Complete Well-Check</Text>
                        </TouchableOpacity>
                      </View>

                    </View>
                  );
                })()}

                {/* Lower-right footer: Track in Action Plan (defaults checked before first explicit toggle) */}
                {(() => {
                  const existingId = (dto?.id || '').trim();
                  const mvar = existingId ? `wellcheck_${existingId}` : '';
                  const trackingExplicitlyDisabled = dto?.trackAction === false;
                  const hasTrackedAction = existingId
                    ? (activeProfile?.actionsstate ?? []).some((a: AdminActionDto) => (a.mvar || '').toLowerCase() === mvar.toLowerCase())
                    : false;
                  const currentlyTracked = trackingExplicitlyDisabled
                    ? false
                    : (existingId ? hasTrackedAction : true);
                  return (
                    <View style={styles.schedTrackFooterRow}>
                      <TouchableOpacity
                        onPress={() => {
                          toggleWellCheckTrackAction(
                            event.eventId,
                            (event.name || '').trim() || 'Well-Check',
                            dueDate ? dueDate.toISOString().slice(0, 10) : '',
                            !currentlyTracked,
                          );
                        }}
                      >
                        <View style={styles.schedDispCheckRow}>
                          <View style={[styles.schedDispCheckbox, currentlyTracked && styles.schedDispCheckboxOn]}>
                            {currentlyTracked && <Text style={styles.schedDispCheckMark}>✓</Text>}
                          </View>
                          <Text style={styles.schedTrackFooterText}>Track in Action Plan</Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  );
                })()}

              </View>
            );
          })() : (
            <View style={styles.noCompletedContainer}>
              <Text style={styles.noCompletedText}>🎉 All scheduled well-check visits are complete.</Text>
            </View>
          )}
        </View>

        {/* ── Card 2: Full schedule overview ────────────────────────────────── */}
        <View style={styles.section}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 }}>
            <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>📋 Schedule</Text>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }]}
              onPress={() => setSchedShowPast(v => !v)}
            >
              <Text style={[styles.navButtonText, { fontSize: 18 }]}>{schedShowPast ? '▲' : '▼'}</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.completedCard, { paddingVertical: 4 }]}>
            {eventRows.length === 0 ? (
              <View style={styles.noCompletedContainer}>
                <Text style={styles.noCompletedText}>No events defined in this schedule.</Text>
              </View>
            ) : (
              eventRows
                .filter(r => schedShowPast || !r.isPast)
                .map(({ event, dueDate, dto, isPast, isCompleted }) => {
                const isNext = upcomingRow?.event.eventId === event.eventId;
                const accentColor =
                  dto?.status === 'completed'          ? statusColors.completed :
                  dto?.status === 'missed'             ? statusColors.missed :
                  dto?.status === 'timed_out'          ? statusColors.timed_out :
                  dto?.status === 'appt_completed'     ? statusColors.appt_completed :
                  dto?.status === 'snapshot_completed' ? statusColors.snapshot_completed :
                  isNext ? '#3498db' :
                  isPast ? '#e67e22' :
                  '#bdc3c7';

                const statusLabel = (() => {
                  if (!dto?.status) return null;
                  switch (dto.status) {
                    case 'timed_out':          return '⏰ Timed Out';
                    case 'appt_completed':     return '📝 Appt Attended';
                    case 'snapshot_completed': return '📸 Snapshot Completed';
                    case 'completed':          return '✅ Completed';
                    case 'missed':             return '❌ Missed';
                    default: return dto.status.charAt(0).toUpperCase() + dto.status.slice(1);
                  }
                })();

                // Gray (future/unscheduled) gets no background; green (completed) gets a stronger tint
                const rowBg = accentColor === '#bdc3c7' ? undefined
                  : accentColor === statusColors.completed ? accentColor + '28'
                  : accentColor + '18';

                return (
                  <View
                    key={event.eventId}
                    style={[styles.schedEventRow, rowBg ? { backgroundColor: rowBg } : undefined]}
                  >
                    <View style={{ flex: 1 }}>
                      <View style={styles.schedHeaderRow}>
                        <Text style={[styles.schedEventName, { flex: 1, marginBottom: 0 }]}>
                          {statusIcon(dto, isPast, isNext)}{'  '}{event.name}
                        </Text>
                        <Text style={styles.schedDueLabel}>Due: {fmtDate(dueDate)}</Text>
                      </View>
                      {/* Row 2: Vaccine tracking — summary line + optional inline editor */}
                      {vaccineInfoEnabled && (event.vaccines ?? []).length > 0 && (() => {
                        const vaxList = (event.vaccines ?? []).map(v => v.name);
                        const TERM = ['completed', 'missed', 'timed_out'];
                        const isTerminal = TERM.includes(dto?.status ?? '');
                        // Future = not past and not terminal (Next Well-Check is also future for missing determination)
                        const isFuture = !isPast && !isTerminal;
                        const receivedNames = new Set(
                          (activeProfile?.vaccinetrack?.events ?? []).map((e: { name: string }) => e.name)
                        );
                        const received = vaxList.filter(v => receivedNames.has(v));
                        const missing  = vaxList.filter(v => !receivedNames.has(v));
                        const isEditing = schedVaxEditEventId === event.eventId;
                        const allEditChecked = isEditing && vaxList.length > 0 && vaxList.every(v => schedVaxEditChecked.has(v));
                        return (
                          <View style={{ marginTop: 4 }}>
                            {isFuture ? (
                              /* Future event — just list expected vaccines */
                              <Text style={[styles.schedEventDetail, { marginBottom: 0, color: '#7f8c8d' }]}>
                                {'Vaccines: '}{vaxList.join(', ')}{' expected'}
                              </Text>
                            ) : (
                              /* Past/current event — show Received / Missing + optional Edit */
                              <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: 4 }}>
                                {received.length > 0 && (
                                  <Text style={[styles.schedEventDetail, { marginBottom: 0, color: '#27ae60' }]}>
                                    {'Received: '}{received.join(', ')}
                                  </Text>
                                )}
                                {missing.length > 0 && (
                                  <Text style={[styles.schedEventDetail, { marginBottom: 0, color: '#e74c3c' }]}>
                                    {'Missing: '}{missing.join(', ')}
                                  </Text>
                                )}
                                {missing.length > 0 && (
                                  <TouchableOpacity
                                    style={{ marginLeft: 6 }}
                                    onPress={() => {
                                      if (isEditing) {
                                        setSchedVaxEditEventId(null);
                                      } else {
                                        setSchedVaxEditChecked(new Set(received));
                                        setSchedVaxEditEventId(event.eventId);
                                      }
                                    }}
                                  >
                                    <Text style={{ fontSize: 11, color: '#3498db' }}>
                                      {isEditing ? 'Cancel' : 'Edit'}
                                    </Text>
                                  </TouchableOpacity>
                                )}
                              </View>
                            )}
                            {/* Inline editor — shown when Edit is active (past/current events only) */}
                            {!isFuture && isEditing && (
                              <View style={{ marginTop: 6 }}>
                                {/* Select All row */}
                                <View style={styles.schedDispVaxHeader}>
                                  <Text style={[styles.schedDispVaxTitle, { fontSize: 11 }]}>💉 Mark received</Text>
                                  <TouchableOpacity
                                    style={styles.schedDispSelectAll}
                                    onPress={() => setSchedVaxEditChecked(allEditChecked ? new Set() : new Set(vaxList))}
                                  >
                                    <Text style={styles.schedDispSelectAllText}>
                                      {allEditChecked ? 'Deselect All' : 'Select All'}
                                    </Text>
                                  </TouchableOpacity>
                                </View>
                                {/* Checkbox chip grid */}
                                <View style={styles.schedDispVaxGrid}>
                                  {vaxList.map(v => (
                                    <TouchableOpacity
                                      key={v}
                                      style={styles.schedDispVaxChip}
                                      onPress={() => {
                                        const next = new Set(schedVaxEditChecked);
                                        if (next.has(v)) next.delete(v); else next.add(v);
                                        setSchedVaxEditChecked(next);
                                      }}
                                    >
                                      <View style={[styles.schedDispCheckbox, schedVaxEditChecked.has(v) && styles.schedDispCheckboxOn]}>
                                        {schedVaxEditChecked.has(v) && <Text style={styles.schedDispCheckMark}>✓</Text>}
                                      </View>
                                      <Text style={styles.schedDispVaxName}>{v}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </View>
                                {/* Update button */}
                                <TouchableOpacity
                                  style={[styles.schedDispSelectAll, { marginTop: 6, alignSelf: 'flex-start' }]}
                                  onPress={() => {
                                    if (!activeProfile) return;
                                    const recordDate = dto?.date || new Date().toISOString().slice(0, 10);
                                    const existingVT: VaccineTrackDto = activeProfile.vaccinetrack ?? { events: [] };
                                    // Remove previous entries for these vaccines then add current checked ones
                                    const kept = existingVT.events.filter((e: { name: string }) => !vaxList.includes(e.name));
                                    const added = Array.from(schedVaxEditChecked).map(name => ({
                                      name,
                                      date: recordDate,
                                      eventId: (event.vaccines ?? []).find(v => v.name === name)?.eventId ?? '',
                                    }));
                                    updateProfile(activeProfile.id, { vaccinetrack: { events: [...kept, ...added] } });
                                    markProfileDirty(activeProfile.id, `VaccineTrack updated for event ${event.eventId}`);
                                    setSchedVaxEditEventId(null);
                                  }}
                                >
                                  <Text style={styles.schedDispSelectAllText}>Update</Text>
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                        );
                      })()}
                      {/* Row 3: Height / Weight tracking */}
                      {trackHeightWeight && (() => {
                        const TERM = ['completed', 'missed', 'timed_out'];
                        const isTerminal = TERM.includes(dto?.status ?? '');
                        // Future = not past and not terminal (Next Well-Check is also future for missing determination)
                        const isFuture = !isPast && !isTerminal;
                        if (isFuture) return null;
                        const hwEntry: GrowthDataDto | undefined = (() => {
                          const gt = (activeProfile?.growthtrack?.data ?? []) as GrowthDataDto[];
                          return gt.find(d => d.eventId === event.eventId);
                        })();
                        const hasHW = !!(hwEntry && (hwEntry.height?.trim() || hwEntry.weight?.trim()));
                        const heightLabel = measurementUnits === 'metric' ? 'cm' : 'in';
                        const weightLabel = measurementUnits === 'metric' ? 'kg' : 'lb';
                        const dob = activeProfile?.dob;
                        const isUnder1Month = dob ? (() => {
                          const d = new Date(dob); const n = new Date();
                          return !isNaN(d.getTime()) && (n.getTime() - d.getTime()) < 30 * 24 * 60 * 60 * 1000;
                        })() : false;
                        const showOz = isUnder1Month && measurementUnits === 'english';
                        const isHwEditing = schedHwEditEventId === event.eventId;
                        const normalizeHalf = (v: string) => v === '' ? '' : v.includes('.') ? v : `${v}.0`;
                        const webInputStyle = (w?: number) => ({ width: w ?? 72, border: '1px solid #d0d7de', borderRadius: 6, padding: '6px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', textAlign: 'right' as const });
                        return (
                          <View style={{ marginTop: 4 }}>
                            {hasHW ? (
                              <Text style={[styles.schedEventDetail, { marginBottom: 0 }]}>
                                {'📏 Height: '}{hwEntry!.height}{' '}{heightLabel}{'  Weight: '}{hwEntry!.weight}{' '}{weightLabel}
                                {showOz && hwEntry!.weightOz ? `  ${hwEntry!.weightOz} oz` : ''}
                              </Text>
                            ) : (
                              <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: 4 }}>
                                <Text style={[styles.schedEventDetail, { marginBottom: 0, color: '#e74c3c' }]}>
                                  {'Missing: Height & Weight'}
                                </Text>
                                <TouchableOpacity
                                  style={{ marginLeft: 6 }}
                                  onPress={() => {
                                    if (isHwEditing) {
                                      setSchedHwEditEventId(null);
                                    } else {
                                      setSchedHwEditHeight('');
                                      setSchedHwEditWeight('');
                                      setSchedHwEditWeightOz('');
                                      const dueDateIso = dueDate
                                        ? `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}`
                                        : (dto?.date ?? '');
                                      setSchedHwEditDate(dueDateIso);
                                      setSchedHwEditEventId(event.eventId);
                                    }
                                  }}
                                >
                                  <Text style={{ fontSize: 11, color: '#3498db' }}>
                                    {isHwEditing ? 'Cancel' : 'Edit'}
                                  </Text>
                                </TouchableOpacity>
                              </View>
                            )}
                            {/* Inline H/W editor */}
                            {isHwEditing && (
                              <View style={{ marginTop: 6 }}>
                                <View style={styles.schedDispVaxHeader}>
                                  <Text style={[styles.schedDispVaxTitle, { fontSize: 11 }]}>📏 Height / Weight</Text>
                                </View>
                                <View style={styles.schedDispVaxGrid}>
                                  {/* Height */}
                                  <View style={styles.schedDispMeasureGroup}>
                                    {Platform.OS === 'web' ? (
                                      // @ts-ignore
                                      <input type="number" value={schedHwEditHeight}
                                        step={measurementUnits === 'metric' ? '1' : '0.5'}
                                        onChange={(e: any) => setSchedHwEditHeight(
                                          measurementUnits === 'metric' ? e.target.value : normalizeHalf(e.target.value)
                                        )}
                                        placeholder="0" style={webInputStyle()}
                                      />
                                    ) : (
                                      <TextInput style={styles.schedDispMeasureInput}
                                        value={schedHwEditHeight} onChangeText={setSchedHwEditHeight}
                                        placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                    )}
                                    <Text style={styles.schedDispMeasureUnit}>{heightLabel}</Text>
                                  </View>
                                  {/* Weight */}
                                  <View style={styles.schedDispMeasureGroup}>
                                    {Platform.OS === 'web' ? (
                                      // @ts-ignore
                                      <input type="number" value={schedHwEditWeight}
                                        step={showOz ? '1' : '0.5'}
                                        onChange={(e: any) => setSchedHwEditWeight(
                                          showOz ? e.target.value : normalizeHalf(e.target.value)
                                        )}
                                        placeholder="0" style={webInputStyle()}
                                      />
                                    ) : (
                                      <TextInput style={styles.schedDispMeasureInput}
                                        value={schedHwEditWeight} onChangeText={setSchedHwEditWeight}
                                        placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                    )}
                                    <Text style={styles.schedDispMeasureUnit}>{weightLabel}</Text>
                                  </View>
                                  {/* oz — under 1 month + English units only */}
                                  {showOz && (
                                    <View style={styles.schedDispMeasureGroup}>
                                      {Platform.OS === 'web' ? (
                                        // @ts-ignore
                                        <input type="number" value={schedHwEditWeightOz}
                                          step="0.5" min="0" max="15.5"
                                          onChange={(e: any) => setSchedHwEditWeightOz(normalizeHalf(e.target.value))}
                                          placeholder="0" style={webInputStyle(60)}
                                        />
                                      ) : (
                                        <TextInput style={[styles.schedDispMeasureInput, { width: 60 }]}
                                          value={schedHwEditWeightOz} onChangeText={setSchedHwEditWeightOz}
                                          placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad" />
                                      )}
                                      <Text style={styles.schedDispMeasureUnit}>oz</Text>
                                    </View>
                                  )}
                                </View>
                                {/* Update button */}
                                <TouchableOpacity
                                  style={[styles.schedDispSelectAll, { marginTop: 6, alignSelf: 'flex-start' }]}
                                  onPress={() => {
                                    if (!activeProfile) return;
                                    const recordDate = schedHwEditDate || dto?.date || new Date().toISOString().slice(0, 10);
                                    const h = schedHwEditHeight.trim();
                                    const w = schedHwEditWeight.trim();
                                    const oz = schedHwEditWeightOz.trim();
                                    if (h || w) {
                                      const newEntry: GrowthDataDto = { date: recordDate, height: h, weight: w, eventId: event.eventId, ...(showOz && oz ? { weightOz: oz } : {}) };
                                      // Replace any existing growthtrack entry for this event, update wellchecks status
                                      const existingWC: WellCheckDto[] = (activeProfile.wellchecks ?? []) as WellCheckDto[];
                                      const currentGT: GrowthTrackDto = activeProfile.growthtrack ?? { data: [] };
                                      const kept = (currentGT.data as GrowthDataDto[]).filter(d => d.eventId !== event.eventId);
                                      updateProfile(activeProfile.id, {
                                        wellchecks: existingWC, // no change to wellchecks — hgtWgt removed
                                        growthtrack: { data: [...kept, newEntry] },
                                      });
                                      markProfileDirty(activeProfile.id, `H/W updated for event ${event.eventId}`);
                                    }
                                    setSchedHwEditEventId(null);
                                  }}
                                >
                                  <Text style={styles.schedDispSelectAllText}>Update</Text>
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                        );
                      })()}
                      {/* Appt date + status + location — only when a date has been saved */}
                      {dto?.date ? (
                        <View style={{ marginTop: 2 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Text style={styles.schedEventDetail}>📅 {dto.date}</Text>
                            {statusLabel ? (
                              <Text style={[styles.schedEventStatus, { color: accentColor, marginLeft: 8, marginBottom: 0 }]}>
                                {statusLabel}
                              </Text>
                            ) : null}
                          </View>
                          {dto?.location ? <Text style={styles.schedEventDetail}>📍 {dto.location}</Text> : null}
                        </View>
                      ) : null}
                    </View>
                  </View>
                );
              })
            )}
          </View>
        </View>

        {/* ── Card 3: Vaccine Key ───────────────────────────────────────────── */}
        {vaccineInfoEnabled && (() => {
          // Build a map from vaccineId → VaccineDto for O(1) lookup
          const vaccineMap = new Map(wellCheckVaccines.map(v => [v.vaccineId, v]));
          // Collect unique vaccineIds in the order they first appear across events
          const uniqueVaccineIds = Array.from(new Set(
            (wellCheckSchedule?.events ?? []).flatMap(e => (e.vaccines ?? []).map(v => v.vaccineId))
          ));
          const allVaccines = uniqueVaccineIds
            .map(id => vaccineMap.get(id))
            .filter((v): v is VaccineDto => !!v);
          if (allVaccines.length === 0) return null;

          return (
            <View style={styles.vaccineKeyFooter}>
              {allVaccines.map(vax => (
                <View key={vax.vaccineId} style={styles.vaccineKeyRow}>
                  <Text style={styles.vaccineKeyAcronym}>{vax.name}</Text>
                  <Text style={styles.vaccineKeyName}>{vax.dName}</Text>
                </View>
              ))}
            </View>
          );
        })()}

      </ScrollView>
    );
  };

  const renderSnapshotContent = () => {
    const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    // ── Milestones freshness ──────────────────────────────────────────────────
    const devComputed = activeProfile?.devresultcomputed;
    const milestoneTimes = devComputed
      ? Object.values(devComputed).filter((v): v is string => !!v)
      : [];
    const milestonesUpToDate = milestoneTimes.length > 0 &&
      milestoneTimes.every(ts => now - new Date(ts).getTime() < TWO_WEEKS_MS);

    // ── Concerns freshness ───────────────────────────────────────────────────
    const sentinels = (activeProfile?.concernsstate ?? []).filter(
      (c: { mvarid: string }) => c.mvarid.startsWith('domain_viewed_')
    );
    const concernsUpToDate = sentinels.length > 0 &&
      sentinels.every((s: { lastUpdated: string }) => now - new Date(s.lastUpdated).getTime() < TWO_WEEKS_MS);

    // ── Well-Check assessment ────────────────────────────────────────────────
    const wellCheckDef = assessmentDefinitions.find(d => d.sourceId === '4');
    const wellCheckInProgress = availableAssessments.find(a =>
      a.sourceId === '4' || assessmentDefinitions.find(d => d.surveyId === a.surveyId)?.sourceId === '4'
    );
    // Exclude snapshots that have been closed after a visit was completed
    // recSurvey stores sourceId — match against both surveyId and sourceId
    const wellCheckCompleted = completedAssessments.find(a => {
      const def = assessmentDefinitions.find(d => d.surveyId === a.recSurvey || d.sourceId === a.recSurvey);
      return (def?.sourceId === '4' || a.recSurvey === '4') && a.recStatus !== 'closed';
    });
    const wellCheckDone = !!wellCheckCompleted;
    // True when a snapshot assessment (sourceId=4) is currently in progress
    const snapshotInProgress = !!wellCheckInProgress;

    // Resolve the well-check event name stored in the completed snapshot's responseData
    const snapshotWellCheckEventId = wellCheckCompleted?.responseData?.find(d => d.name === 'wellcheckeventid')?.value ?? null;
    const snapshotWellCheckEventName = snapshotWellCheckEventId
      ? wellCheckSchedule?.events?.find(e => e.eventId === snapshotWellCheckEventId)?.name ?? null
      : null;

    const formatDate = (utmOrDate?: string) => {
      try {
        return new Date(utmOrDate || '').toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      } catch { return utmOrDate || 'Unknown date'; }
    };

    return (
      <ScrollView style={styles.contentContainer}>
        {/* Snapshot Checklist */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {snapshotEventName ? `📋 Snapshot Checklist for ${snapshotEventName}` : '📋 Snapshot Checklist'}
          </Text>
          <View style={styles.checklistCard}>
            {(() => {
              // ── Actions freshness (computed here so it's available for the step logic) ──
              const ACTION_TAB_KEYS = ['goals', 'manage', 'advocate', 'provider'];
              const concernsStateAll: { mvarid: string; lastUpdated?: string }[] = activeProfile?.concernsstate ?? [];
              const actionSentinels = ACTION_TAB_KEYS.map(k =>
                concernsStateAll.find(c => c.mvarid === `action_tab_viewed_${k}`)
              );
              const actionsUpToDate = actionSentinels.length > 0 &&
                actionSentinels.every(s => s?.lastUpdated && now - new Date(s.lastUpdated).getTime() < TWO_WEEKS_MS);

              // Progressive gate: each step only activates once all prior steps are done.
              // When a snapshot is in progress, steps 1-3 are locked to prevent mid-flight changes.
              const step1Active = !snapshotInProgress;                          // Milestones — locked while snapshot active
              const step2Active = !snapshotInProgress && milestonesUpToDate;   // Concerns
              const step3Active = !snapshotInProgress && step2Active && concernsUpToDate; // Actions
              const step4Active = snapshotInProgress || (step3Active && actionsUpToDate); // Assessment — always active when snapshot running

              const lockedHint = snapshotInProgress ? 'Snapshot in progress — complete or cancel to update' : 'Complete the step above first';

              // Helper: renders a grayed-out locked row
              const renderLockedRow = (label: string, hint?: string, isLast = false) => (
                <View style={[styles.snapshotCheckRow, isLast && { borderBottomWidth: 0 }, { opacity: 0.38 }]}>
                  <View style={styles.snapshotCheckPlaceholder} />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.snapshotCheckLabel}>{label}</Text>
                    <Text style={styles.snapshotCheckHint}>{hint ?? lockedHint}</Text>
                  </View>
                </View>
              );

              return (
                <>
                  {/* Step 1: Update Milestones */}
                  {step1Active ? milestonesUpToDate && (onSnapshotTabChange ?? onTabChange) ? (
                    <TouchableOpacity
                      style={styles.snapshotCheckRow}
                      onPress={() => (onSnapshotTabChange ?? onTabChange)!('Milestones')}
                    >
                      <Text style={styles.snapshotGreenCheck}>✅</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.snapshotCheckLabel}>Milestones Updated</Text>
                        <Text style={styles.snapshotCheckHint}>All domains updated within 2 weeks</Text>
                      </View>
                      <Text style={{ fontSize: 22, color: '#aaa', marginLeft: 8 }}>›</Text>
                    </TouchableOpacity>
                  ) : (
                  <View style={styles.snapshotCheckRow}>
                    {milestonesUpToDate
                      ? <Text style={styles.snapshotGreenCheck}>✅</Text>
                      : <View style={styles.snapshotCheckPlaceholder} />}
                    <View style={{ flex: 1 }}>
                      <Text style={styles.snapshotCheckLabel}>
                        {milestonesUpToDate ? 'Milestones Updated' : 'Update Milestones'}
                      </Text>
                      <Text style={styles.snapshotCheckHint}>
                        {milestonesUpToDate
                          ? 'All domains updated within 2 weeks'
                          : 'Ensure milestone answers are up to date'}
                      </Text>
                    </View>
                    {!milestonesUpToDate && (onSnapshotTabChange ?? onTabChange) && (
                      <TouchableOpacity
                        style={styles.snapshotNavBtn}
                        onPress={() => (onSnapshotTabChange ?? onTabChange)!('Milestones')}
                      >
                        <Text style={styles.snapshotNavBtnText}>▶ Update</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  ) : renderLockedRow('Update Milestones')}

                  {/* Step 2: Update Concerns */}
                  {step2Active ? (
                    concernsUpToDate && (onSnapshotTabChange ?? onTabChange) ? (
                      <TouchableOpacity
                        style={styles.snapshotCheckRow}
                        onPress={() => (onSnapshotTabChange ?? onTabChange)!('Concerns')}
                      >
                        <Text style={styles.snapshotGreenCheck}>✅</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.snapshotCheckLabel}>Concerns Updated</Text>
                          <Text style={styles.snapshotCheckHint}>All domains reviewed within 2 weeks</Text>
                        </View>
                        <Text style={{ fontSize: 22, color: '#aaa', marginLeft: 8 }}>›</Text>
                      </TouchableOpacity>
                    ) : (
                    <View style={styles.snapshotCheckRow}>
                      {concernsUpToDate
                        ? <Text style={styles.snapshotGreenCheck}>✅</Text>
                        : <View style={styles.snapshotCheckPlaceholder} />}
                      <View style={{ flex: 1 }}>
                        <Text style={styles.snapshotCheckLabel}>
                          {concernsUpToDate ? 'Concerns Updated' : 'Update Concerns'}
                        </Text>
                        <Text style={styles.snapshotCheckHint}>
                          {concernsUpToDate
                            ? 'All domains reviewed within 2 weeks'
                            : 'Review and update concern tracking'}
                        </Text>
                      </View>
                      {!concernsUpToDate && (onSnapshotTabChange ?? onTabChange) && (
                        <TouchableOpacity
                          style={styles.snapshotNavBtn}
                          onPress={() => (onSnapshotTabChange ?? onTabChange)!('Concerns')}
                        >
                          <Text style={styles.snapshotNavBtnText}>▶ Update</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    )
                  ) : renderLockedRow('Update Concerns', lockedHint)}

                  {/* Step 3: Update Actions */}
                  {step3Active ? (
                    actionsUpToDate && (onSnapshotTabChange ?? onTabChange) ? (
                      <TouchableOpacity
                        style={styles.snapshotCheckRow}
                        onPress={() => (onSnapshotTabChange ?? onTabChange)!('Actions')}
                      >
                        <Text style={styles.snapshotGreenCheck}>✅</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.snapshotCheckLabel}>Actions Updated</Text>
                          <Text style={styles.snapshotCheckHint}>All action groups reviewed within 2 weeks</Text>
                        </View>
                        <Text style={{ fontSize: 22, color: '#aaa', marginLeft: 8 }}>›</Text>
                      </TouchableOpacity>
                    ) : (
                    <View style={styles.snapshotCheckRow}>
                      {actionsUpToDate
                        ? <Text style={styles.snapshotGreenCheck}>✅</Text>
                        : <View style={styles.snapshotCheckPlaceholder} />}
                      <View style={{ flex: 1 }}>
                        <Text style={styles.snapshotCheckLabel}>
                          {actionsUpToDate ? 'Actions Updated' : 'Update Actions'}
                        </Text>
                        <Text style={styles.snapshotCheckHint}>
                          {actionsUpToDate
                            ? 'All action groups reviewed within 2 weeks'
                            : 'Review and confirm action plan items'}
                        </Text>
                      </View>
                      {!actionsUpToDate && (onSnapshotTabChange ?? onTabChange) && (
                        <TouchableOpacity
                          style={styles.snapshotNavBtn}
                          onPress={() => (onSnapshotTabChange ?? onTabChange)!('Actions')}
                        >
                          <Text style={styles.snapshotNavBtnText}>▶ Update</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    )
                  ) : renderLockedRow('Update Actions', lockedHint)}

                  {/* Step 4: Well-Check Assessment */}
                  {step4Active ? (
                    <View style={[styles.snapshotCheckRow, { alignItems: 'center', borderBottomWidth: 0 }]}>
                      {wellCheckDone && !snapshotInProgress
                        ? <Text style={styles.snapshotGreenCheck}>✅</Text>
                        : <View style={styles.snapshotCheckPlaceholder} />}
                      <View style={{ flex: 1 }}>
                        <Text style={styles.snapshotCheckLabel}>
                          {wellCheckDef ? wellCheckDef.dName : 'Well-Check Assessment'}
                          {wellCheckDone && !snapshotInProgress ? ' — Completed' : ''}
                        </Text>
                        {wellCheckDone && !snapshotInProgress && (
                          <Text style={styles.snapshotCheckHint}>
                            Completed {formatDate(wellCheckCompleted!.recUTM || wellCheckCompleted!.recDate)}
                          </Text>
                        )}
                        {!wellCheckDone && !snapshotInProgress && !storage && (
                          <Text style={styles.snapshotCheckHint}>Assessment unavailable</Text>
                        )}
                      </View>
                      {/* Continue button — snapshot is actively in progress */}
                      {snapshotInProgress && storage && (
                        <TouchableOpacity
                          style={styles.snapshotAssessmentBtn}
                          onPress={() => handleStartAssessment(
                            wellCheckInProgress!.segmentList || wellCheckInProgress!.sourceId, true
                          )}
                        >
                          <Text style={styles.snapshotNavBtnText}>▶ Continue</Text>
                        </TouchableOpacity>
                      )}
                      {/* Start New — prior snapshot exists but none in progress */}
                      {!snapshotInProgress && wellCheckDone && storage && (
                        <TouchableOpacity
                          style={styles.snapshotAssessmentBtn}
                          onPress={() => {
                            if (wellCheckDef) {
                              handleStartAssessment(wellCheckDef.surveybList, false);
                            } else {
                              handleStartAssessment('4', false);
                            }
                          }}
                        >
                          <Text style={styles.snapshotNavBtnText}>▶ Start New</Text>
                        </TouchableOpacity>
                      )}
                      {/* Start — no prior snapshot, none in progress */}
                      {!snapshotInProgress && !wellCheckDone && storage && (
                        <TouchableOpacity
                          style={styles.snapshotAssessmentBtn}
                          onPress={() => {
                            if (wellCheckDef) {
                              handleStartAssessment(wellCheckDef.surveybList, false);
                            } else {
                              handleStartAssessment('4', false);
                            }
                          }}
                        >
                          <Text style={styles.snapshotNavBtnText}>▶ Start</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ) : renderLockedRow('Well-Check Assessment', lockedHint, true)}
                </>
              );
            })()}
          </View>
        </View>

        {/* Snapshot Report */}
        <View style={styles.section}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>📊 Snapshot Report</Text>
              {snapshotWellCheckEventName ? (
                <Text style={{ fontSize: 12, color: '#7f8c8d', marginTop: 2 }}>{snapshotWellCheckEventName}</Text>
              ) : null}
            </View>
            {snapshotReportHtml && !snapshotInProgress && (
              <TouchableOpacity
                style={[{ paddingVertical: 6, paddingHorizontal: 14, backgroundColor: appConfig.getNavButtonColor(), borderRadius: 6 }]}
                onPress={() => setShowSnapshotShareModal(true)}
              >
                <Text style={{ color: '#ffffff', fontWeight: '600', fontSize: 13 }}>Share</Text>
              </TouchableOpacity>
            )}
          </View>
          {snapshotReportHtml && !snapshotInProgress && (
            <ShareReportModal
              visible={showSnapshotShareModal}
              onClose={() => setShowSnapshotShareModal(false)}
              reportName="Snapshot Report"
              onCreatePdf={onViewPdf ? () => onViewPdf(snapshotReportHtml, 'Snapshot Report') : undefined}
              profileConnections={activeProfile?.connections ?? []}
              apiService={apiService}
            />
          )}
          {snapshotInProgress ? (
            <View style={[styles.completedCard, { borderLeftWidth: 0 }]}>
              <View style={styles.noCompletedContainer}>
                <Text style={styles.noCompletedText}>
                  ⏳ Snapshot in progress… Complete the assessment above to generate a new report.
                </Text>
              </View>
            </View>
          ) : wellCheckCompleted ? (
            <View style={[styles.completedCard, { borderLeftWidth: 0, padding: 0, overflow: 'hidden' }]}>
              {snapshotReportHtml ? (
                <WebView
                  source={{ html: wrapReportHtml(snapshotReportHtml) }}
                  style={{ height: snapshotReportWebViewHeight || 200, width: '100%' }}
                  scrollEnabled={false}
                  injectedJavaScript={`
                    (function () {
                      function post() {
                        window.ReactNativeWebView.postMessage(
                          String(Math.max(document.body.scrollHeight, document.documentElement.scrollHeight))
                        );
                      }
                      post();
                      new MutationObserver(post).observe(document.body, { subtree: true, childList: true });
                    })();
                    true;
                  `}
                  onMessage={e => setSnapshotReportWebViewHeight(Number(e.nativeEvent.data))}
                />
              ) : (
                <View style={styles.completedButton}>
                  <Text style={styles.completedButtonDate}>
                    📅 Completed: {formatDate(wellCheckCompleted.recUTM || wellCheckCompleted.recDate)}
                  </Text>
                  <Text style={[styles.completedButtonDate, { color: '#95a5a6' }]}>
                    Generating report…
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <View style={[styles.completedCard, { borderLeftWidth: 0 }]}>
              <View style={styles.noCompletedContainer}>
                <Text style={styles.noCompletedText}>
                  No well-check report yet.{' '}Complete the assessment above to generate a report.
                </Text>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    );
  };

  const renderPreviousContent = () => (
    <ScrollView style={styles.contentContainer}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📊 Completed Assessments</Text>
        
        <View style={styles.completedCard}>
          <Text style={styles.completedTitle}>Assessment History</Text>
          <Text style={styles.completedDescription}>
            View reports from previously completed assessments
          </Text>
          
          {storage && completedAssessments.length > 0 && hasPHI ? (
            <View style={styles.completedList}>
              {completedAssessments.map((assessment, index) => {
                // Find matching AdminAssessmentDto by linking recSurvey to surveyId or sourceId
                // (recSurvey stores adminState.sourceId, so must check both fields)
                const matchedDefinition = assessmentDefinitions.find(
                  def => def.surveyId === assessment.recSurvey || def.sourceId === assessment.recSurvey
                );
                
                // Use AdminAssessmentDto.dName if found, otherwise use fallback
                const assessmentName = matchedDefinition 
                  ? matchedDefinition.dName 
                  : (assessment.recSurvey === '1987' ? 'PHQ-9 Depression Screening' : 
                     assessment.recSurvey === '1988' ? 'GAD-7 Anxiety Screening' : 
                     assessment.recAlias || `Assessment ${assessment.recSurvey}`);

                // For Well-Check Snapshots, resolve the linked well-check event name
                const isSnapshotAssessment = matchedDefinition?.sourceId === '4' || assessment.recSurvey === '4';
                const wellCheckEvtId = isSnapshotAssessment
                  ? assessment.responseData?.find(d => d.name === 'wellcheckeventid')?.value ?? null
                  : null;
                const wellCheckEvtName = wellCheckEvtId
                  ? wellCheckSchedule?.events?.find(e => e.eventId === wellCheckEvtId)?.name ?? null
                  : null;

                // Format RecUTM timestamp
                const completedDate = (() => {
                  try {
                    const date = new Date(assessment.recUTM || assessment.recDate);
                    return date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch {
                    return assessment.recUTM || assessment.recDate || 'Unknown date';
                  }
                })();
                
                return (
                  <TouchableOpacity
                    key={assessment.recId || index}
                    style={styles.completedButton}
                    onPress={() => handleShowReport(assessment.recId)}
                  >
                    <View style={styles.completedButtonContent}>
                      <Text style={styles.completedButtonTitle}>{assessmentName}</Text>
                      {wellCheckEvtName ? (
                        <Text style={[styles.completedButtonDate, { color: '#3498db', marginBottom: 2 }]}>
                          📅 {wellCheckEvtName}
                        </Text>
                      ) : null}
                      <Text style={styles.completedButtonDate}>
                        📅 Completed: {completedDate}
                      </Text>
                      <Text style={styles.completedButtonRecId}>
                        🆔 Record: {assessment.recId}
                      </Text>
                    </View>
                    <View style={styles.completedButtonArrow}>
                      <Text style={styles.arrowText}>→</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : (
            <View style={styles.noCompletedContainer}>
              <Text style={styles.noCompletedText}>
                {storage ? 'No completed assessments yet' : 'Assessment history unavailable'}
              </Text>
            </View>
          )}
        </View>
      </View>

    </ScrollView>
  );

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      {/* Fixed Header with Navigation Controls - full width background, constrained content */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
        <View style={styles.domainTitleRow}>
          <Text style={styles.domainTitle}>
            {selectedTab.icon}  {selectedTab.name}
          </Text>
        </View>
        <View style={styles.navigationRow}>
          <View style={styles.leftNavigationControls} />
          <View style={styles.centerDescription}>
            <Text style={styles.domainDescription}>{selectedTab.description}</Text>
          </View>
          <View style={styles.rightNavigationControls}>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === 0 && styles.navButtonDisabled]}
              onPress={handlePrevTab}
              disabled={selectedTabIndex === 0}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === WELLVISIT_TABS.length - 1 && styles.navButtonDisabled]}
              onPress={handleNextTab}
              disabled={selectedTabIndex === WELLVISIT_TABS.length - 1}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </View>

      {/* Tab Menu - full width background, content constrained to headerWidthMax */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
        {WELLVISIT_TABS.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              selectedTabIndex === index && { backgroundColor: appConfig.getNavButtonColor() }
            ]}
            onPress={() => setSelectedTabIndex(index)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.tabIcon}>{tab.icon}</Text>
              <Text style={[styles.tabText, selectedTabIndex === index && styles.selectedTabText]}>
                {tab.tabLabel ?? tab.name}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {/* Main Content Card */}
      <View style={[styles.cardWrapper, { paddingTop: 8 }]}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          {selectedTab.key === 'Schedule' ? renderScheduleContent() : selectedTab.key === 'Snapshot' ? renderSnapshotContent() : selectedTab.key === 'Requests' ? renderRequestsContent() : renderPreviousContent()}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
  },
  section: {
    marginHorizontal: 14,
    marginVertical: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  appointmentCard: {
    backgroundColor: '#ffffff',
    padding: 14,
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  appointmentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  appointmentDate: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 4,
  },
  appointmentLocation: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 4,
  },
  appointmentStatus: {
    fontSize: 14,
    color: '#27ae60',
    fontWeight: '500',
  },
  checklistCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  checklistTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  checklistItem: {
    fontSize: 14,
    color: '#34495e',
    marginBottom: 6,
    paddingLeft: 4,
  },
  questionsCard: {
    backgroundColor: '#f0f8ff',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#3498db',
  },
  questionItem: {
    fontSize: 14,
    color: '#2c3e50',
    marginBottom: 8,
    paddingLeft: 4,
  },
  assessmentSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
  },
  assessmentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  assessmentDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 12,
    lineHeight: 20,
  },
  assessmentButton: {
    backgroundColor: '#3498db',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  assessmentButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  assessmentButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  assessmentButtonTextDisabled: {
    color: '#7f8c8d',
  },
  assessmentSubtitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
    marginTop: 12,
  },
  assessmentProgress: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 4,
  },
  newAssessmentSection: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
  },
  newAssessmentButton: {
    backgroundColor: '#27ae60',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  // Completed assessments styles
  completedCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#9b59b6',
  },
  completedTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  completedDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 16,
    lineHeight: 20,
  },
  completedList: {
    marginTop: 8,
  },
  completedButton: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    flexDirection: 'row',
    alignItems: 'center',
  },
  completedButtonContent: {
    flex: 1,
  },
  completedButtonTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 6,
  },
  completedButtonDate: {
    fontSize: 13,
    color: '#7f8c8d',
    marginBottom: 4,
  },
  completedButtonRecId: {
    fontSize: 12,
    color: '#95a5a6',
  },
  completedButtonArrow: {
    marginLeft: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 18,
    color: '#9b59b6',
    fontWeight: 'bold',
  },
  noCompletedContainer: {
    backgroundColor: '#f8f9fa',
    padding: 24,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e1e8ed',
    borderStyle: 'dashed',
  },
  noCompletedText: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  snapshotCheckRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 12,
  },
  snapshotCheckLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 2,
  },
  snapshotCheckLabelDone: {
    color: '#27ae60',
  },
  snapshotCheckHint: {
    fontSize: 12,
    color: '#7f8c8d',
    lineHeight: 16,
  },
  snapshotAssessmentBtn: {
    backgroundColor: appConfig.getNavButtonColor(),
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    flexShrink: 0,
  },
  snapshotGreenCheck: {
    fontSize: 22,
    marginTop: 1,
    flexShrink: 0,
  },
  snapshotCheckPlaceholder: {
    width: 22,
    flexShrink: 0,
  },
  snapshotNavBtn: {
    backgroundColor: '#10b981',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    flexShrink: 0,
  },
  snapshotNavBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },

  // ── Schedule tab ────────────────────────────────────────────────────────────
  // Header row: event name + due date side by side
  schedHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  schedDueLabel: {
    fontSize: 12,
    color: '#7f8c8d',
    flexShrink: 0,
    marginLeft: 8,
  },
  vaccineKeyFooter: {
    marginTop: 4,
    marginLeft: 40,
    marginRight: 10,
    paddingBottom: 24,
  },
  vaccineKeyRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 2,
  },
  vaccineKeyAcronym: {
    fontSize: 11,
    fontWeight: '600',
    color: '#8a9ab0',
    width: 72,
    flexShrink: 0,
  },
  vaccineKeyName: {
    fontSize: 11,
    color: '#aab3be',
    flex: 1,
    flexWrap: 'wrap',
  },
  // Inline field rows: label + input side by side
  schedFieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 26,
    marginTop: 8,
    gap: 6,
  },
  schedTrackFooterRow: {
    marginTop: 10,
    alignSelf: 'flex-end',
  },
  schedTrackFooterText: {
    fontSize: 12,
    color: '#5b6472',
    fontWeight: '600',
  },
  schedInlineLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#34495e',
    width: 86,
    flexShrink: 0,
  },
  schedTypeOptions: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  schedTypeOptionChip: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  schedTypeOptionText: {
    fontSize: 12,
  },
  schedInlineInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#fafbfc',
  },
  schedInlineDateButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#fafbfc',
    justifyContent: 'center',
  },
  schedInlineDateButtonText: {
    fontSize: 13,
    color: '#2c3e50',
  },
  schedInlineDateButtonPlaceholder: {
    color: '#8f9aa6',
  },
  schedDispMeasureGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  schedDispMeasureInput: {
    width: 72,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#fafbfc',
    textAlign: 'right',
  },
  schedDispMeasureUnit: {
    fontSize: 13,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  // Checkbox row (Prescriptions / Referrals enable toggles)
  schedDispCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  schedDispCheckLabel: {
    fontSize: 13,
    color: '#34495e',
  },
  // Outcome Yes/No radio
  schedDispOutcomeQuestion: {
    fontSize: 13,
    color: '#34495e',
    marginBottom: 8,
  },
  schedDispRadioGroup: {
    flexDirection: 'row',
    gap: 24,
  },
  schedDispRadioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  schedDispRadio: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#adb5bd',
    backgroundColor: '#fff',
  },
  schedDispRadioOn: {
    borderColor: '#27ae60',
    backgroundColor: '#27ae60',
  },
  schedDispRadioLabel: {
    fontSize: 13,
    color: '#34495e',
    fontWeight: '500',
  },
  schedComputedStatus: {
    fontSize: 13,
    fontWeight: '700',
  },
  // Info badge (snapshot pending)
  schedInfoBadge: {
    backgroundColor: '#f0f4ff',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#c7d7f5',
  },
  schedInfoBadgeText: {
    fontSize: 13,
    color: '#5b7db1',
    fontWeight: '500',
  },
  // Primary action button (Start / Continue Snapshot)
  schedActionBtn: {
    backgroundColor: '#10b981',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  schedActionBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },
  // Completed / Missed close-out row
  schedStatusRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  schedStatusBtn: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#f6f8fa',
    alignItems: 'center',
    justifyContent: 'center',
  },
  schedStatusBtnText: {
    fontSize: 13,
    color: '#34495e',
    fontWeight: '600',
  },
  schedApptBadge: {
    backgroundColor: '#ebf5fb',
    borderWidth: 1.5,
    borderColor: '#3498db',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
  },
  schedApptBadgeText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#3498db',
  },
  schedDispositionForm: {
    marginTop: 10,
    backgroundColor: '#f8faff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d0e4f5',
    padding: 10,
  },
  schedDispToggle: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#f6f8fa',
  },
  schedDispToggleOn: {
    backgroundColor: '#d4edda',
    borderColor: '#27ae60',
  },
  schedDispToggleText: {
    fontSize: 13,
    color: '#34495e',
    fontWeight: '600',
  },
  schedDispToggleTextOn: {
    color: '#27ae60',
  },
  // Vaccination section
  schedDispVaxSection: {
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e8edf2',
    paddingBottom: 10,
    paddingLeft: 8,
  },
  schedDispVaxHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  schedDispVaxTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2c3e50',
  },
  schedDispSelectAll: {
    borderWidth: 1,
    borderColor: '#3498db',
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 10,
    backgroundColor: '#ebf5fb',
  },
  schedDispSelectAllText: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '600',
  },
  schedDispVaxGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  schedDispVaxChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 4,
    backgroundColor: '#f0f4f8',
    borderWidth: 1,
    borderColor: '#d0d7de',
  },
  schedDispCheckbox: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderColor: '#adb5bd',
    borderRadius: 3,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  schedDispCheckboxOn: {
    backgroundColor: '#27ae60',
    borderColor: '#27ae60',
  },
  schedDispCheckMark: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '700',
    lineHeight: 13,
  },
  schedDispVaxName: {
    fontSize: 12,
    color: '#2c3e50',
    marginLeft: 6,
  },
  // Schedule overview rows
  schedEventRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f2f5',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  schedEventName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 2,
  },
  schedEventDue: {
    fontSize: 12,
    color: '#7f8c8d',
    marginBottom: 2,
  },
  schedEventDetail: {
    fontSize: 12,
    color: '#555',
    marginBottom: 1,
  },
  schedEventStatus: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
});
